import { computed, DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { catchError, debounceTime, defer, EMPTY, filter, finalize, forkJoin, from, fromEvent, map, Observable, of, shareReplay, Subject, switchMap, tap, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { isVisualQuestion, mkAns, mkScheda, SYMBOLS } from '../shared/constants';
import { activeQuestions, questionIdsByCode } from '../shared/helpers/active-questions.helper';
import { buildPerimetroText, buildSchedaText, buildTranscript } from '../shared/helpers/transcript';
import { fileToBlock, urlToB64 } from '../shared/helpers/file';
import { chipCounts, insertInlineMarkers, pushChipEvent, undoLastChipEvent } from '../shared/helpers/chip-events';
import type {
  ConsentoMetodo,
  ContentBlock,
  GruppoRicerca,
  IAnswer,
  IAnswerRow,
  IAnswersSnapshot,
  IArchProfilo,
  IAttachment,
  IChipEvent,
  IConsentoDraft,
  IDeviationFlag,
  IFaseZeroEsiti,
  IGenerationState,
  IFaseZeroTema,
  IIncongruenza,
  IParticipant,
  IPesoElemento,
  IPonderazione,
  IProfiles,
  IInterviewStart,
  ILookups,
  IQuestion,
  VisualChoices,
  IStartInterviewBody,
  IScheda,
  ISubAnswer,
  IVisualGalleryEntry,
  Percorso,
  Screen,
  SchedaMode,
  SessionMode,
  UserContent,
} from '../shared/models';
import { ApiService, ITranscription } from './api.service';
import { ArchTemplateService } from './arch-template/arch-template.service';
import { AuthService } from './auth/auth.service';
import { ConfigStore } from './config-store';
import { ConsensoService } from './consenso/consenso.service';
import { ExportService, IExportMeta } from './export.service';
import { FaseZeroService } from './fase-zero/fase-zero.service';
import { GenerationService } from './generation/generation.service';
import { InterviewService } from './interview/interview.service';
import { InterviewTimerService } from './interview/interview-timer.service';
import { ParticipantService } from './participant/participant.service';
import { PonderazioneService } from './ponderazione/ponderazione.service';
import { QuestionSetService } from './question-set/question-set.service';
import { TavoleService } from './tavole/tavole';
import { RecorderService } from './recorder.service';

export type PdfState = 'idle' | 'saving' | 'saved' | 'error';

// Schermate del flusso intervista (da /scheda in poi): qui l'id nel path deve sempre esserci
// finché la pratica ha un id (v. _syncIdInUrl). Fuori (intro, interviews, admin, survey) no.
const FLOW_SCREENS: ReadonlySet<string> = new Set<Screen>([
  'scheda',
  'document',
  'interview',
  'processing',
  'chiarimenti',
  'validation',
  'profile',
  'ponderazione',
  'profondita',
  'rating',
]);

// Path della schermata quando l'id è nel segmento: 'interview' diventa 'interviews'
// (/interviews/<id>, coerente con l'archivio /interviews); le altre restano se stesse.
const flowPath = (screen: string): string => ('interview' === screen ? 'interviews' : screen);

// Separatore tra il markdown delle ipotesi e il JSON delle incongruenze nella risposta di Fase A.
const FASE_A_INCONGRUENZE_MARKER = '===INCONGRUENZE_JSON===';

type SubAnsVal = string | IChipEvent[] | boolean | VisualChoices;

// Percorso Veloce (30/10/2026): sostituisce ponderazione ed esiti dei chiarimenti nel messaggio del
// Profilo Architettonico — su quel percorso non esistono, e gli indici IIP/IBAP non si stimano.
const VELOCE_ARCH_INSTRUCTION = `\n\n[PERCORSO VELOCE]\nIntervista breve: nessuna ponderazione del cliente, nessun chiarimento, nessun indice IIP/IBAP. Il Profilo Abitativo qui sopra è solo una base interna, al cliente non viene consegnato. Nel JSON: "iipA": null, "ibapAtteso": null, "previsioneIndici": [], "peso": null in ogni voce della legenda, e "temiCongruenti"/"temiChiariti"/"temiDaVerificare": "Non previsto (Percorso Veloce)". Per l'allocazione delle risorse usa la sola sequenza tecnica.`;

// Fase Zero (Prompt 1 Rev. 9.3): il cancello di chiarimento è nel prompt; qui si vincola solo la
// SERIALIZZAZIONE del caso "domande al posto del profilo" a JSON, per poterlo distinguere in modo
// affidabile dal markdown del profilo (stesso schema di FASE_A_INCONGRUENZE_MARKER: il formato è
// plumbing dell'app, la sostanza resta nel prompt in /admin).
const FASE_ZERO_FORMAT_INSTRUCTION = `\n\n[FORMATO FASE ZERO]\nSe la FASE ZERO del tuo prompt ti impone di NON generare il profilo e restituire le Domande di chiarimento, restituisci SOLO un oggetto JSON valido (nessun testo prima o dopo, nessun code fence): {"domandeChiarimento":[{"tema":"titolo in linguaggio corrente","domanda":"la domanda da porre ora al cliente, aperta e non giudicante","perche":"una riga, solo per il somministratore"}]} — da 1 a 5 voci, le più importanti prima. Se invece generi il profilo, restituisci SOLO il markdown come da prompt (mai JSON), incluso il blocco "Esiti dei chiarimenti" con la riga di Conteggio.`;

// Validazione incrociata del "Quadro di affidabilità dei dati" (Specifica Prompt 2 Rev. 11.2 §3),
// bloccante post-generazione: i numeri del Quadro devono coincidere col Conteggio applicativo e
// ogni tema «da verificare» deve avere la sua Domanda aperta. Ritorna null se tutto coerente,
// altrimenti il motivo del blocco. Esportata pura per il test.
// Match del titolo nelle domande aperte: prima per sottostringa esatta (normalizzata), poi per
// sovrapposizione di radici di parola (≥60%) — _esitiTxt impone la citazione testuale, ma il
// modello a volte parafrasa/accorcia i titoli lunghi (visto in reale: «Pavimento e camminare
// scalzi: freddo o caldo sotto i piedi?») e il blocco deve scattare solo per temi davvero senza
// domanda, non per una riformulazione.
// ponytail: radici = parola tagliata a max(4, len-3) — copre le flessioni italiane (camminare/
// camminando, caldo/calda); embedding/semantica se mai servirà davvero.
export function verificaQuadroAffidabilita(arch: IArchProfilo, esiti: IFaseZeroEsiti): string | null {
  const num = (s: string): number => {
    const m = /\d+/.exec(s ?? '');
    return null === m ? -1 : Number(m[0]);
  };
  if (
    num(arch.temiCongruenti) !== esiti.nCongruenti ||
    num(arch.temiChiariti) !== esiti.nChiariti ||
    num(arch.temiDaVerificare) !== esiti.nNonChiariti
  ) {
    return `numeri del Quadro di affidabilità diversi dal Conteggio applicativo (attesi ${esiti.nCongruenti} · ${esiti.nChiariti} · ${esiti.nNonChiariti})`;
  }
  const norm = (s: string): string =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9 ]/g, ' ')
      .replace(/ +/g, ' ')
      .trim();
  const domande = arch.domandeAperte.map((d) => norm(`${d.tema} ${d.approfondimento}`));
  const haDomanda = (titolo: string): boolean => {
    const t = norm(titolo);
    if (domande.some((d) => d.includes(t))) {
      return true;
    }
    const stems = t
      .split(' ')
      .filter((w) => w.length >= 4)
      .map((w) => w.slice(0, Math.max(4, w.length - 3)));
    return (
      0 !== stems.length &&
      domande.some((d) => stems.filter((s) => d.includes(s)).length / stems.length >= 0.6)
    );
  };
  const mancante = esiti.temi.find((t) => 'non_chiarito' === t.stato && !haDomanda(t.titolo));
  return undefined === mancante
    ? null
    : `il tema da verificare «${mancante.titolo}» non ha una Domanda aperta corrispondente`;
}

// JSON è più verboso del markdown libero usato finora per lo stesso contenuto (chiavi ripetute,
// virgolette, escaping): un Profilo Architettonico completo (più ambienti, ciascuno con
// prescrizioni/tabelle) ha già troncato sia a 8192 che a 24000 su un'intervista molto ricca
// (26+ preferenze dichiarate) in prova reale. Richiede l'header beta
// 'anthropic-beta: output-128k-2025-02-19' lato cloudflare/worker.js (già impostato, verificato
// ridistribuito) per poter salire oltre 8192 — combinato con la sinteticità imposta in
// ARCH_JSON_FORMAT_CRUSCOTTO/AMBIENTI, non solo con più token.
// Il budget è configurato in environment.archMaxTokens e include i token di thinking:
// si paga il generato, non il cap.

// Polling del job BE del Profilo Architettonico (v. _pollArchJob): cadenza e tempo massimo
// prima di dichiarare il job perso (allineato allo STALE_JOB_MS del BE, 30 min + margine).
const ARCH_JOB_POLL_MS = 5000;
const ARCH_JOB_TIMEOUT_MS = 35 * 60 * 1000;
// Il cruscotto NON scala col numero di stanze, ma scala con l'indice di elementi (legenda +
// tabellaRecepimento + previsioneIndici ripetono ogni PA-§n/PD-n) --- un'intervista ricca (32
// elementi, in prova reale) ha già troncato con un tetto di 12000 a metà di "notaLibera",
// mandando il merge sul fallback raw-concat (v. Correzione 14/16). max_tokens è solo un
// SOFFITTO: non costa nulla se il modello si ferma prima, quindi niente sconto qui, stesso
// tetto della metà ambienti.

// Chiusura comune alle due istruzioni di formato sotto: entrambe le risposte sono parsate
// indipendentemente (v. runFaseB), quindi entrambe devono restare JSON valido da sole.
const ARCH_JSON_FORMAT_FOOTER = `Mappa priorità: 🔴→"alta", 🟡→"media", 🟢→"bassa". Tutti i valori testuali in italiano, nel registro del tuo prompt. Il JSON è solo la serializzazione: la sostanza (tracciabilità, Principi, IIP-A/IBAP come stime in corso di validazione) resta quella del tuo prompt.
ATTENZIONE — il JSON deve restare sintatticamente valido: quando riporti una citazione letterale del cliente (ammesse in questo documento) dentro un valore stringa, usa le virgolette alte “…” o l'apice singolo, MAI la virgoletta dritta " senza escape — altrimenti la stringa si interrompe a metà. Se proprio serve una virgoletta dritta dentro un valore, scrivi \\" (backslash prima delle doppie apici). Nessun testo, commento o code fence fuori dall'oggetto JSON.`;

// Istruzione di formato per il Profilo Architettonico (Fase B): il prompt in /admin (Prompt 2)
// resta quello — Principi, tracciabilità PA-§n/PD-n, priorità 🔴🟡🟢 — solo la SERIALIZZAZIONE
// diventa JSON invece di markdown, per poter renderizzare il layout editoriale a due colonne
// (cruscotto + schede-ambiente) invece del markdown generico. Vedi shared/models.ts IArchProfilo.
// Metà 1/2 (generazione e rigenerazione, in parallelo con ARCH_JSON_FORMAT_AMBIENTI): tutto il Profilo
// Architettonico TRANNE le schede-ambiente e il gradiente, che non scalano col numero di stanze
// e quindi restano il collo di bottiglia — separarli permette alle due generazioni di correre
// in parallelo invece che in coda nella stessa chiamata (v. runFaseB).
const ARCH_JSON_FORMAT_CRUSCOTTO = `FORMATO DI OUTPUT — restituisci SOLO un oggetto JSON valido (nessun testo prima o dopo, nessun code fence, nessun markdown), con questa forma esatta. Questa è SOLO la metà "cruscotto" del Profilo Architettonico: le schede-ambiente (gradiente + ambienti) sono generate in una chiamata separata, NON includerle qui. Rispetta i limiti di parole indicati campo per campo: sono un vincolo tecnico, non una richiesta di superficialità.
{
  "titolo": string (es. "Famiglia Rossi — Appartamento, Milano"), "sottotitolo": string (es. "La casa che accompagna una nuova famiglia"),
  "concept": string, "parolaChiave": string,
  "iipA": number (0-100, stima del sistema in corso di validazione), "ibapAtteso": number (delta, ancorato ai 15 item core del questionario IBAP),
  "segmentoPrimario": string, "segmentoPrimarioDesc": string, "segmentoSecondario": string|null, "segmentoSecondarioDesc": string|null,
  "sintesiIdentitaria": string (archetipo confermato + i 3 elementi irrinunciabili, max 10 righe),
  "legenda": [{"codice":"PA-01|PD-01","testo":"...","sezione":"...","peso":number|null (0-100, SOLO una tua stima del peso relativo di questo elemento se puoi dedurla dal colloquio, altrimenti null — non è una ponderazione dichiarata dal cliente, quella schermata non esiste ancora)}] (riprende l'Indice degli elementi del Profilo Abitativo, numerazione identica, mai reinferita),
  "mappaTensioni": [{"conflitto":"...","riferimenti":["PA-.."],"mediazione":"...","prevalenza":"..."}] (vuoto se un solo abitante),
  "tabellaRecepimento": [{"pdRef":"PD-01","preferenza":"... (max 12 parole)","scheda":"... (nome ambiente/sezione, non una frase)","esito":"... (max 12 parole)"}] (una riga per ogni PD-n, nessuna esclusa),
  "previsioneIndici": [{"codice":"PA-..|PD-..","priorita":"alta","macroarea":"AFF|COG|COM","iip":1-5,"ibap":1-5,"motivazione":"... (max 15 parole)"}] (SOLO prescrizioni 🔴 alta priorità — le medie non entrano in questa tabella, restano nella scheda ambiente),
  "proposteAudaci": [{"proposta":"...","derivaDa":["PA-.."],"perche":"..."}] (max 3, [] se il profilo non le sostiene),
  "domandeAperte": [{"tema":"...","approfondimento":"..."}] (max 7),
  "garanzie": [{"richiesta":"...","nota":"..."}],
  "noteTecniche": ["..."] ([] se assenti),
  "temiCongruenti": "es. 4 su 5", "temiChiariti": "es. 1", "temiDaVerificare": "es. 1 (tema)",
  "profondita": {"dichiarata":"leggera|media|pesante" (livello d'intervento più plausibile per il perimetro descritto, TUA STIMA dal colloquio — non esiste ancora la scelta del cliente), "quotaAPerdere":"alta|parziale|nessuna" (lavorazioni da rifare se si passasse a un livello superiore), "notaLibera": string|null, "ordineAntispreco": ["...", "..."] (sequenza tecnica obbligata, solo le fasi pertinenti a questo progetto, nell'ordine strutture e impianti -> involucro e isolamenti -> superfici -> finiture -> arredo e tessile), "posticipo": {"rimandabile": ["..."], "nonRimandabile": ["..."], "predisposizioniOra": ["..."]}}
}
${ARCH_JSON_FORMAT_FOOTER}`;

// Metà 2/2: SOLO le schede-ambiente + il loro ordine di esposizione. Tenute insieme (invece di
// spezzare anche "gradiente" in una terza chiamata) perché il gradiente è solo un riordino dei
// nomi già prodotti qui: chiederlo altrove rischierebbe nomi/grafia leggermente diversi tra le
// due risposte.
const ARCH_JSON_FORMAT_AMBIENTI = `FORMATO DI OUTPUT — restituisci SOLO un oggetto JSON valido (nessun testo prima o dopo, nessun code fence, nessun markdown), con questa forma esatta. Questa è SOLO la metà "schede-ambiente" del Profilo Architettonico: il cruscotto (titolo, concept, legenda, tabelle, ecc.) è generato in una chiamata separata, NON includerlo qui. Rispetta i limiti di parole indicati campo per campo.
{
  "gradiente": ["nome ambiente", ...] (sequenza dal più esposto al più intimo, stessi nomi usati sotto in "ambienti"),
  "ambienti": [{
    "nome":"...", "diProgetto": string|null (valorizzato solo se l'ambiente non è nel rilievo della Scheda),
    "inSintesi":"... (max 20 parole)", "atmosfera":"... (2-4 parole, es. \"Calda, avvolgente, luminosa\")",
    "comeRigenera":"... (max 20 parole, lente ART/SRT in parole comuni, MAI la stessa formula tra due schede)",
    "rispondeAlBisogno":"... (2-5 parole, es. \"Ricarica dopo il lavoro\")",
    "layoutDistribuzione":"... (max 40 parole, prosa — ogni scelta rilevante cita [PA-§n]/[PD-n] inline dove serve)",
    "materialiFiniturePalette":"... (max 30 parole, prosa)",
    "luceNaturaleArtificiale":"... (max 30 parole, prosa)",
    "scelteSelezionate": ["...", "..."] (elenco puntato, "Scelte già selezionate dal cliente" — [] se il cliente non si è espresso per questo ambiente),
    "erroriDaEvitare": string|null (max 20 parole, solo se rilevante), "verificaFattibilita": string|null (max 20 parole, solo se rilevante),
    "percheQuoteRef": ["PA-.."], "dalColloquio": string|null (solo se ci sono segnali paraverbali/non verbali rilevanti su questo ambiente, max 25 parole),
    "prioritaBenessere": "alta"|"media"|"da pianificare"|null (tua stima di quanto pesa nell'insieme del progetto il bisogno a cui questa scheda risponde; null se non stimabile)
  }]
}
${ARCH_JSON_FORMAT_FOOTER}`;

// Stato condiviso dell'intera seduta + orchestrazione della generazione (mirror dell'App React).
@Injectable({ providedIn: 'root' })
export class SessionStore {
  // --- private ---
  private readonly _api: ApiService = inject(ApiService);
  private readonly _archTemplate: ArchTemplateService = inject(ArchTemplateService);
  private readonly _recorder: RecorderService = inject(RecorderService);
  private readonly _auth: AuthService = inject(AuthService);
  private readonly _config: ConfigStore = inject(ConfigStore);
  private readonly _export: ExportService = inject(ExportService);
  private readonly _interview: InterviewService = inject(InterviewService);
  private readonly _generation: GenerationService = inject(GenerationService);
  private readonly _participant: ParticipantService = inject(ParticipantService);
  private readonly _ponderazione: PonderazioneService = inject(PonderazioneService);
  private readonly _questionSet: QuestionSetService = inject(QuestionSetService);
  private readonly _tavoleSvc: TavoleService = inject(TavoleService);
  private readonly _consenso: ConsensoService = inject(ConsensoService);
  private readonly _faseZero: FaseZeroService = inject(FaseZeroService);
  private readonly _toast: MessageService = inject(MessageService);
  private readonly _router: Router = inject(Router);
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);
  private readonly _interviewTimer: InterviewTimerService = inject(InterviewTimerService);
  private readonly _mergedTranscriptSignal: WritableSignal<string> = signal<string>('');
  // Blocchi immagine (base64) delle tavole scelte: calcolati una sola volta per seduta di
  // generazione (Fase A + le 2 chiamate di Fase B non devono ri-scaricare/ri-codificare
  // le stesse immagini), invalidati a ogni startSession.
  private _visualBlocksCache: ContentBlock[] | null = null;
  // Istante (epoch ms) di avvio della registrazione in corso: serve a correlare il timestamp
  // di ogni tap della pulsantiera all'offset (in secondi) del segmento Whisper corrispondente.
  private _recStartAt: number | null = null;
  // (partecipante, domanda) su cui la registrazione è stata avviata: chip e trascrizione devono
  // agganciarsi lì anche se nel frattempo si naviga altrove (v. commento su _setAnsAt).
  private _recPos: { pi: number; qi: number } | null = null;
  // Salvataggio incrementale delle risposte su DB (interview_answers_app): un tick a ogni cambio
  // domanda / trascrizione arrivata / avvio generazione. Debounce + switchMap: i tick ravvicinati
  // collassano in un solo upsert e vince sempre lo stato più recente. Silenzioso su errore (rete
  // di sicurezza, non deve disturbare l'intervista); la prima scrittura crea riga intervista +
  // partecipanti via _ensureCode$ (stesso guard di persistInterview: mai duplicati).
  private readonly _answersPersist$: Subject<void> = new Subject<void>();
  // Già avvisato che l'intervista è in sola lettura (RLS): il toast esce una volta sola,
  // l'autosave riparte a ogni cambio domanda e spammerebbe.
  private _readOnlyWarned = false;
  private readonly _answersPersistSub = this._answersPersist$
    .pipe(
      debounceTime(800),
      switchMap(() =>
        this._ensureCode$().pipe(
          switchMap(() => {
            const id = this.interviewIdSignal();
            if (null === id) {
              return of(undefined);
            }
            // Meta riallineati a ogni snapshot: la riga nasce al primo salvataggio (magari dalla
            // scheda), quando nomi/percorso/data possono essere ancora provvisori.
            // Prima gli allegati sul bucket (upload once: se path già presente è un no-op), POI lo
            // snapshot — che porta solo il path, mai il b64 (v. IAttachment e _answersSnapshot).
            return this._uploadAttachments$().pipe(
              switchMap(() =>
                forkJoin([
                  this._interview.saveAnswers(id, this._answersSnapshot()),
                  this._interview.updateMeta(id, {
                    client_names: this.nomiSignal(),
                    percorso: this.percSignal(),
                    interview_date: this.dateSignal() || null,
                  }),
                  // Proiezione riga-per-riga accanto allo snapshot (0031): un errore qui non deve
                  // far fallire il salvataggio vero, che è lo snapshot.
                  this._persistAnswerRows$(id).pipe(catchError(() => of(undefined))),
                ]),
              ),
              map(() => undefined),
            );
          }),
          catchError((err: unknown) => {
            // RLS nega la scrittura (403): non-proprietario (es. store manager) su intervista
            // altrui — errore parlante invece del silenzio; gli altri errori restano silenziosi.
            if (
              false === this._readOnlyWarned &&
              err instanceof Error &&
              err.message.includes('row-level security')
            ) {
              this._readOnlyWarned = true;
              this._error("Solo l'account che ha effettuato l'intervista può modificarla");
            }
            return of(undefined);
          }),
        ),
      ),
      takeUntilDestroyed(this._destroyRef),
    )
    .subscribe();

  // --- public ---
  // modalità
  public readonly sessionModeSignal: WritableSignal<SessionMode> = signal<SessionMode>('interview');
  public readonly schedaModeSignal: WritableSignal<SchedaMode> = signal<SchedaMode>('form');

  // dati seduta
  public readonly archSignal: WritableSignal<string> = signal<string>('');
  public readonly somministratoreSignal: WritableSignal<string> = signal<string>('');
  public readonly personaIntervistataSignal: WritableSignal<string> = signal<string>('');
  public readonly ruoloSignal: WritableSignal<string> = signal<string>('');
  public readonly dateSignal: WritableSignal<string> = signal<string>(new Date().toISOString().slice(0, 10));
  public readonly percSignal: WritableSignal<Percorso> = signal<Percorso>('');
  public readonly durSignal: WritableSignal<string> = signal<string>('');
  // Braccio di ricerca (§7/§8 doc DEV_v7): null = intervista fuori dal percorso di ricerca.
  // Condiviso da tutti i partecipanti della sessione (un nucleo familiare non si divide tra bracci).
  public readonly gruppoRicercaSignal: WritableSignal<GruppoRicerca | null> = signal<GruppoRicerca | null>(null);

  // scheda abitativa + seconda trascrizione
  public readonly schedaSignal: WritableSignal<IScheda> = signal<IScheda>(mkScheda());
  public readonly schedaDocSignal: WritableSignal<IAttachment | null> = signal<IAttachment | null>(null);
  public readonly assistantTranscriptSignal: WritableSignal<string> = signal<string>('');

  // percorso documento (risposte già pronte)
  public readonly docNomiSignal: WritableSignal<string> = signal<string>('');
  public readonly answersDocSignal: WritableSignal<string> = signal<string>('');
  public readonly answersFileSignal: WritableSignal<IAttachment | null> = signal<IAttachment | null>(null);

  // intervista
  public readonly partsSignal: WritableSignal<IParticipant[]> = signal<IParticipant[]>([
    { name: 'Cliente', answers: mkAns(this._config.questionsSignal()) },
  ]);
  // Bozza di consenso informato (Chi_Compila_Cosa_e_Quando_Rev2_1ab §5), per indice di
  // partsSignal(): raccolto qui perché i participant_app non esistono ancora a questo punto
  // della seduta (creati solo in _ensureCode$). Registrata in consent_app non appena il
  // participant_app corrispondente viene creato — vedi _registerPendingConsensi$.
  public readonly consentoDraftSignal: WritableSignal<Record<number, IConsentoDraft>> = signal({});
  public readonly activePSignal: WritableSignal<number> = signal<number>(0);
  public readonly activeQSignal: WritableSignal<number> = signal<number>(0);
  // target del recorder/AI condiviso: 'main' = risposta principale, number = indice sub-risposta (spunto)
  public readonly recTargetSignal: WritableSignal<'main' | number | null> = signal(null);
  public readonly transcribingTargetSignal: WritableSignal<'main' | number | null> = signal(null);
  public readonly aiTargetSignal: WritableSignal<'main' | number | null> = signal(null);
  public readonly isRecSignal: Signal<boolean> = computed(() => this.recTargetSignal() === 'main');
  public readonly isTranscribingSignal: Signal<boolean> = computed(() => this.transcribingTargetSignal() === 'main');
  public readonly isAISignal: Signal<boolean> = computed(() => this.aiTargetSignal() === 'main');

  // profili + elaborazione
  public readonly profMdSignal: WritableSignal<IProfiles> = signal<IProfiles>({
    cliente: '',
    architetto: '',
  });
  // Profilo Architettonico strutturato, derivato da profMdSignal().architetto (JSON, v. Fase B).
  // null se il parsing fallisce (es. interviste archiviate prima di questo formato): il template
  // ricade sul markdown grezzo in quel caso.
  public readonly archProfiloSignal: Signal<IArchProfilo | null> = computed(() =>
    this._parseArchProfilo(this.profMdSignal().architetto),
  );
  // Il Profilo esiste ed è visitabile: è la condizione che fa comparire la destinazione «Profili»
  // nella barra «Vai a…» (v. shared/components/goto-bar). Basta l'Abitativo — l'Architettonico può
  // arrivare dopo (ponderazione + Fase B) e /profile si apre lo stesso, sul tab cliente.
  public readonly hasProfileSignal: Signal<boolean> = computed(() => '' !== this.profMdSignal().cliente.trim());
  // L'intervista è avviata: la riga interview_question_set_app esiste (201 di startInterview, o
  // getInterviewStart alla ripresa) e percorso/set di domande sono fissati. È la condizione che
  // fa comparire la destinazione «Questionario» nella barra «Vai a…»: prima dell'avvio la porta
  // alle domande è /profondita, che nella barra è lo step «Percorso». _resumedSignal copre la
  // finestra in cui getInterviewStart è ancora in volo subito dopo un «Riprendi».
  public readonly interviewStartedSignal: Signal<boolean> = computed(
    () => null !== this.interviewStartSignal() || this._resumedSignal(),
  );
  // Percorso Veloce (30/10/2026, scelto solo a mano su /profondita): niente incongruenze di Fase A,
  // niente Fase Zero, niente Ponderazione, niente IIP/IBAP. L'Abitativo resta un passaggio interno
  // (il Prompt 2 numera la legenda dal suo Indice) ma non si mostra né si consegna al cliente.
  public readonly isVeloceSignal: Signal<boolean> = computed(
    () => 'veloce' === this.interviewStartSignal()?.percorso_scelto,
  );
  public readonly procLabelSignal: WritableSignal<string> = signal<string>('');
  public readonly procDetailSignal: WritableSignal<string> = signal<string>('');
  public readonly procProgSignal: WritableSignal<number> = signal<number>(0);
  public readonly faseASignal: WritableSignal<string> = signal<string>('');
  // Divergenze trascrizione/paraverbale/non-verbale rilevate in Fase A (§2 doc DEV_v6).
  public readonly incongruenzeSignal: WritableSignal<IIncongruenza[]> = signal<IIncongruenza[]>([]);

  // Fase Zero — cancello di chiarimento (Prompt 1 Rev. 9.3): temi incongruenti restituiti dal
  // Prompt 1 al posto del profilo, con le domande già formulate. Da NON confondere con
  // incongruenzeSignal (Fase A, buchi/divergenze in validazione): sono due meccanismi distinti
  // (Specifica_Sviluppatori_FaseZero §5) e restano su due schermate diverse.
  public readonly faseZeroTemiSignal: WritableSignal<IFaseZeroTema[]> = signal<IFaseZeroTema[]>([]);
  // Esiti registrati a valle della generazione (prima o seconda passata): fonte del quadro di
  // affidabilità del Profilo Architettonico + riga interview_fase_zero_app.
  public readonly faseZeroEsitiSignal: WritableSignal<IFaseZeroEsiti | null> = signal<IFaseZeroEsiti | null>(null);
  // Sblocco della generazione (Specifica §2, regola di terminazione): ogni tema ha una risposta
  // registrata oppure è già in uno stato finale ([Non ponibile]). Mai bloccante oltre questo.
  public readonly faseZeroReadySignal: Signal<boolean> = computed(() =>
    this.faseZeroTemiSignal().every((t) => 'non_chiarito' === t.stato || '' !== t.risposta.trim()),
  );
  // Contesto della prima passata (validato + note della Fase A), riusato identico nella seconda:
  // la seconda passata NON deve passare da regenProfile (v. Correzione 11), è una ri-esecuzione
  // della stessa Fase B con in più le risposte di chiarimento.
  private _faseZeroCtx: { validato: string; note: string } | null = null;

  // Ponderazione a 100 punti + scelta di profondità (Nota_Sviluppo_v3 §2-§4): schermate
  // raggiungibili da /profile DOPO la prima Fase B (non prima, come da Nota) — il cruscotto
  // dell'architetto già produce archProfiloSignal().legenda/profondita come JSON parsato in modo
  // affidabile, mentre non esiste alcun parser per estrarre gli elementi PA-§n dal markdown
  // grezzo della Fase A. V. 06_profilo_architettonico.tex per la motivazione completa.
  public readonly ponderazioneSignal: WritableSignal<IPonderazione | null> = signal<IPonderazione | null>(null);
  // Avvio intervista (Tre Profondità, blocco C): cosa POST /interview/:id/start ha fissato —
  // versione del questionario, percorso proposto/scelto, perimetro. null finché non si avvia.
  public readonly interviewStartSignal: WritableSignal<IInterviewStart | null> = signal<IInterviewStart | null>(null);
  public readonly startingSignal: WritableSignal<boolean> = signal<boolean>(false);
  // Lookup select_* dell'avvio, tenute qui per dare un NOME leggibile agli impianti nel blocco
  // perimetro dei prompt (la Scheda salva i soli codici). null = non ancora caricate: il blocco
  // ripiega sui codici, che restano comprensibili.
  private readonly _lookupsSignal: WritableSignal<ILookups | null> = signal<ILookups | null>(null);

  // archivio interviste (§5.5): id tecnico + codice parlante + stato salvataggio PDF
  // ponytail: true dopo resumeInterview, consumato da clearResumedSession all'ingresso su /intro.
  // Signal e non campo: interviewStartedSignal lo legge in un computed (v. lì).
  private readonly _resumedSignal: WritableSignal<boolean> = signal<boolean>(false);
  // Epoca di sessione: incrementata a ogni cambio identità (nuova intervista, Riprendi, reset
  // post-Riprendi). I risultati async partiti in un'epoca precedente (Whisper, paraverbale,
  // creazione riga) vengono scartati all'arrivo: mai scrivere dati di una sessione dentro
  // un'altra (era possibile riprendendo una pratica con una trascrizione in volo).
  private _epoch = 0;
  // Creazione riga in volo (condivisa): startSession la lancia subito e il primo autosave può
  // arrivare prima che risolva — senza cache partirebbero due INSERT (due pratiche). Epoca
  // allegata: un cambio di sessione invalida la cache.
  private _rowCreate: { epoch: number; row$: Observable<{ id: string; code: number } | null> } | null = null;
  // Partecipanti/consensi già seminati (un solo tentativo, v. _ensureCode$). La semina resta al
  // primo autosave, NON a startSession: i partecipanti oltre al cliente vengono aggiunti su
  // /interview, seminare prima li perderebbe.
  private _participantsSeeded = false;
  // code → question_app.id della versione FISSATA all'intervista: è la chiave con cui
  // interview_answer_app referenzia la voce (0031). null = nessun set in gioco (flusso documento,
  // intervista avviata prima delle Tre Profondità): senza id non si scrive nessuna riga.
  private _questionIds: Record<string, string> | null = null;
  // id dei participant_app in ordine di ordinal = ordine di partsSignal() (li crea _ensureCode$
  // con quell'ordine). Caricati una volta per seduta, azzerati con la sessione.
  private _participantIds: string[] | null = null;
  public readonly interviewIdSignal: WritableSignal<string | null> = signal<string | null>(null);
  public readonly interviewCodeSignal: WritableSignal<number | null> = signal<number | null>(null);
  public readonly pdfStateSignal: WritableSignal<PdfState> = signal<PdfState>('idle');

  // admin
  public readonly isAdmin: boolean = new URLSearchParams(window.location.search).get('admin') === '1';

  // derivati
  // Domande dell'intervista in corso. Parte dai DEFAULT_QUESTIONS (flusso documento, nessun
  // set); dall'avvio (startInterview) è la proiezione del set pubblicato fissato per la pratica,
  // filtrata su percorso/ambienti/impianti (active-questions.helper); alla ripresa torna dallo
  // snapshot (IAnswersSnapshot.questions), che fotografa esattamente le voci risposte.
  public readonly questionsSignal: WritableSignal<IQuestion[]> = signal<IQuestion[]>(this._config.questionsSignal());
  public readonly promptsSignal = this._config.promptsSignal;
  public readonly ansSignal: Signal<IAnswer | undefined> = computed(
    () => this.partsSignal()[this.activePSignal()]?.answers[this.activeQSignal()],
  );
  public readonly nomiSignal: Signal<string> = computed(() =>
    this.partsSignal()
      .map((p) => p.name)
      .join(', '),
  );

  constructor() {
    this._router.events.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart && false === this._isInterviewFlowUrl(event.url)) {
        this._pauseInterviewTiming();
      }
      if (event instanceof NavigationEnd && true === this._isInterviewFlowUrl(event.urlAfterRedirects)) {
        this._interviewTimer.start(this._interviewTimer.snapshot());
      }
    });
    timer(15000, 15000).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (true === this._interviewTimer.running) {
        this._saveTimingCheckpoint();
      }
    });
    fromEvent(window, 'pagehide').pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._pauseInterviewTiming());
    fromEvent(window, 'pageshow').pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (true === this._isInterviewFlowUrl(this._router.url)) {
        this._interviewTimer.start(this._interviewTimer.snapshot());
      }
    });
    // Rete di sicurezza: QUALUNQUE navigazione (routerLink Home/Indietro, navigateByUrl, ritorno
    // dall'archivio) ri-attacca /<screen>/<id> sulle schermate del flusso finché la pratica ha un
    // id — non solo quelle passate da _go. Il guard dentro _syncIdInUrl evita loop.
    this._router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => this._syncIdInUrl());
  }

  public initialize(): Observable<void> {
    return from(this._auth.whenReady()).pipe(
      tap(() => {
        const session = this._auth.sessionSignal();
        if (true === this._auth.enabled && null === session) {
          return;
        }
        this._restoreFromUrl();
      }),
    );
  }

  // ── navigazione (Router; i path coincidono col tipo Screen) ──
  go(screen: Screen): void {
    this._go(screen);
  }

  // "Nuova intervista" (da /profile o /rating): pulisce i dati del cliente precedente prima di
  // tornare a /intro, così /intro si presenta vuota. Il reset di pratica vero e proprio è comunque
  // garantito da startSession (ogni start = pratica nuova, v. lì). Restano
  // architetto/somministratore/data (stesso operatore, stessa giornata).
  newInterview(): void {
    this._pauseInterviewTiming();
    this._interviewTimer.start(null);
    this._epoch++;
    this._resumedSignal.set(false);
    this._participantsSeeded = false;
    this._questionIds = null;
    this._participantIds = null;
    this._clearClientData();
    this.consentoDraftSignal.set({});
    this.personaIntervistataSignal.set('');
    this._go('intro');
  }

  // Dati del cliente precedente. NON tocca persona/consenso: startSession la chiama quando
  // l'operatore ha già compilato /intro per il cliente nuovo.
  private _clearClientData(): void {
    this.partsSignal.set([{ name: 'Cliente', answers: mkAns(this.questionsSignal()) }]);
    this.activePSignal.set(0);
    this.activeQSignal.set(0);
    this.durSignal.set('');
    this.schedaSignal.set(mkScheda());
    this.schedaDocSignal.set(null);
    this.docNomiSignal.set('');
    this.answersDocSignal.set('');
    this.answersFileSignal.set(null);
    this.assistantTranscriptSignal.set('');
  }

  // Chiude la pratica aperta e riporta l'app allo stato "intervista nuova": risposte, campi di
  // /intro (persona, percorso, gruppo ricerca, architetto, somministratore, data, consenso),
  // identità della pratica e output già generati. La lancia il bottone *Home*, che nel titolo
  // dichiara proprio questo ("chiude l'intervista in corso"): fino al 18/09/2026 chiudeva solo le
  // pratiche RIPRESE, e una pratica avviata da /intro tornava a /intro con tutti i campi ancora
  // compilati (bug segnalato in collaudo).
  // interviewId azzerato subito: un tick di persistenza vagante non deve sovrascrivere lo
  // snapshot della pratica con dati vuoti. Il lavoro non si perde — la riga su DB resta, si
  // rientra da /interviews con "Riprendi".
  closeSession(): void {
    this._pauseInterviewTiming();
    this._interviewTimer.start(null);
    this._epoch++;
    this._resumedSignal.set(false);
    this._participantsSeeded = false;
    this._questionIds = null;
    this._participantIds = null;
    this._clearClientData();
    this.consentoDraftSignal.set({});
    this.personaIntervistataSignal.set('');
    this.percSignal.set('');
    this.gruppoRicercaSignal.set(null);
    this.archSignal.set('');
    this.somministratoreSignal.set('');
    this.dateSignal.set(new Date().toISOString().slice(0, 10));
    this.interviewIdSignal.set(null);
    this.interviewCodeSignal.set(null);
    this.pdfStateSignal.set('idle');
    // Percorso/domande fissati e output già prodotti: senza questi azzeramenti la pratica
    // successiva nascerebbe con /profondita "fissata" e con "Profili" già raggiungibile nella
    // barra Vai a (hasProfileSignal legge profMdSignal).
    this.interviewStartSignal.set(null);
    this.questionsSignal.set(this._config.questionsSignal());
    this.profMdSignal.set({ cliente: '', architetto: '' });
    this.faseASignal.set('');
    this.incongruenzeSignal.set([]);
    this.ponderazioneSignal.set(null);
    this._visualBlocksCache = null;
    this.faseZeroTemiSignal.set([]);
    this.faseZeroEsitiSignal.set(null);
    this._faseZeroCtx = null;
  }

  // Chiamata all'ingresso su /intro (costruttore del componente): dopo un Riprendi da
  // /interviews, arrivare a /intro comunque ci si arrivi (routerLink, back del browser, URL
  // digitato) significa intervista NUOVA. Resta guardata sul solo caso ripreso: /intro è
  // raggiungibile anche senza chiudere niente — /admin ci torna dopo il salvataggio, e una
  // pratica viva aperta da lì non va buttata via (v. Interview.openConfig).
  clearResumedSession(): void {
    if (false === this._resumedSignal()) {
      return;
    }
    this.closeSession();
  }

  // Ogni start da /intro ("Intervista guidata" o "Documento") è SEMPRE una pratica nuova: epoca
  // nuova (invalida la cache _rowCreate → nuovo INSERT), semina partecipanti da rifare, dati del
  // cliente precedente azzerati. Prima la cache restituiva la riga della seduta precedente senza
  // ripristinare id/code nei signal: autosave muto, snapshot e PDF della pratica vecchia
  // sovrascritti. L'unico modo per riprendere una pratica è "Riprendi" da /interviews.
  startSession(mode: SessionMode): void {
    this._pauseInterviewTiming();
    this._interviewTimer.start(null);
    this._epoch++;
    this._resumedSignal.set(false);
    this._participantsSeeded = false;
    this._questionIds = null;
    this._participantIds = null;
    this._clearClientData();
    this.sessionModeSignal.set(mode);
    this._interviewTimer.start('interview' === mode ? { elapsedMs: 0, completed: false, updatedAt: Date.now() } : null);
    this.schedaModeSignal.set(mode === 'document' ? 'upload' : 'form');
    this.interviewIdSignal.set(null);
    this.interviewCodeSignal.set(null);
    // Pratica nuova = non avviata: senza questo reset la seconda intervista della sessione
    // erediterebbe percorso e domande della precedente (/profondita si vedrebbe "fissata").
    this.interviewStartSignal.set(null);
    this.questionsSignal.set(this._config.questionsSignal());
    this.pdfStateSignal.set('idle');
    this._visualBlocksCache = null;
    this.faseZeroTemiSignal.set([]);
    this.faseZeroEsitiSignal.set(null);
    this._faseZeroCtx = null;
    const persona: string = this.personaIntervistataSignal().trim();
    this.partsSignal.update((pp) => pp.map((p, i) => (i !== 0 ? p : { ...p, name: persona || 'Cliente' })));
    // Riga creata SUBITO (non più al primo autosave): l'id esiste prima di qualunque scrittura
    // delle risposte e finisce nel path (/scheda/<id>) — un refresh ripristina la pratica dal DB.
    // Subito dopo anche il primo snapshot risposte: la pratica è "riprendibile" da /interviews
    // fin dall'avvio (nome cliente, modalità, percorso), anche se chiusa prima di compilare
    // qualsiasi cosa. Silenzioso su errore come l'autosave; partecipanti seminati più tardi da
    // _ensureCode$ (i nomi possono ancora cambiare in scheda).
    this._ensureRow$()
      .pipe(
        switchMap((row) =>
          null === row ? of(undefined) : this._interview.saveAnswers(row.id, this._answersSnapshot()),
        ),
        catchError(() => of(undefined)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
    this._go('scheda');
  }

  backFromScheda(): void {
    this._go('intro');
  }

  afterScheda(): void {
    this._go(this._sourceScreen());
  }

  backToSource(): void {
    this._go(this._sourceScreen());
  }

  // ── salvataggio incrementale risposte (interview_answers_app) ──
  // Un tick al pipeline debounced: chiamato a ogni cambio domanda, all'arrivo di una
  // trascrizione/analisi AI e all'avvio della generazione.
  persistAnswers(): void {
    this._answersPersist$.next();
  }

  // Navigazione domande: passa da qui (non da activeQSignal.set diretto) così ogni cambio
  // domanda persiste le risposte su DB.
  goToQuestion(qi: number): void {
    this.activeQSignal.set(qi);
    this.persistAnswers();
  }

  private _answersSnapshot(): IAnswersSnapshot {
    return {
      timing: this._interviewTimer.snapshot(),
      sessionMode: this.sessionModeSignal(),
      percorso: this.percSignal(),
      arch: this.archSignal(),
      somministratore: this.somministratoreSignal(),
      date: this.dateSignal(),
      dur: this.durSignal(),
      personaIntervistata: this.personaIntervistataSignal(),
      parts: this.partsSignal(),
      questions: this.questionsSignal(),
      scheda: this.schedaSignal(),
      schedaMode: this.schedaModeSignal(),
      // MAI il b64 su DB (righe da 30MB, v. IAttachment): nello snapshot va solo il path del
      // bucket. Strip incondizionato: se l'upload non è ancora riuscito il file NON viene
      // persistito (path null) e verrà caricato al prossimo autosave.
      schedaDoc: this._stripB64(this.schedaDocSignal()),
      docNomi: this.docNomiSignal(),
      answersDoc: this.answersDocSignal(),
      answersFile: this._stripB64(this.answersFileSignal()),
    };
  }

  private _isInterviewFlowUrl(url: string): boolean {
    const segments: string[] = url.split(/[/?#]/).filter(Boolean);
    const screen: string = segments[0] ?? '';
    return ('interviews' === screen && undefined !== segments[1]) || FLOW_SCREENS.has(screen);
  }

  private _pauseInterviewTiming(): void {
    const paused: boolean = this._interviewTimer.pause();
    if (true === paused) {
      this._saveTimingCheckpoint();
    }
  }

  private _saveTimingCheckpoint(): void {
    const id: string | null = this.interviewIdSignal();
    if (null === id) {
      return;
    }
    this._interview.saveAnswers(id, this._answersSnapshot())
      .pipe(catchError(() => of(undefined)), takeUntilDestroyed(this._destroyRef)).subscribe();
  }

  private _stripB64(att: IAttachment | null): IAttachment | null {
    return null === att ? null : { ...att, b64: '' };
  }

  // ── risposte riga-per-riga (interview_answer_app, migrazione 0031) ──
  // Proiezione interrogabile dello snapshot: una riga per (intervista, partecipante, voce), con la
  // FK alla voce della versione fissata all'avvio. Serve alle letture trasversali ("tutte le
  // risposte a Q08.1"), impossibili sul blob JSON in cui la risposta è identificata dalla POSIZIONE
  // — posizione che con i tre percorsi e le versioni del set non individua più una domanda.
  // Lo snapshot resta la verità della seduta: chip paraverbali, note dell'architetto e flag
  // "analizzata" non hanno colonna qui e continuano a vivere solo là.
  // Si scrivono TUTTE le voci attive, anche senza risposta: distinguere "domanda somministrata e
  // non risposta" da "domanda non somministrata" è il senso stesso dei tre percorsi. L'upsert
  // sulla chiave (intervista, partecipante, voce) tiene la tabella allineata anche alle
  // cancellazioni.
  private _persistAnswerRows$(interviewId: string): Observable<void> {
    const ids = this._questionIds;
    // Nessun set in gioco (flusso documento, o intervista avviata prima delle Tre Profondità):
    // senza question_app.id non esiste riga da scrivere. Lo snapshot copre comunque tutto.
    if (null === ids) {
      return of(undefined);
    }
    return this._participantIds$(interviewId).pipe(
      switchMap((pids) => {
        const rows = this._answerRows(interviewId, pids, ids);
        return 0 === rows.length ? of(undefined) : this._questionSet.upsertAnswers(rows);
      }),
    );
  }

  // id dei rispondenti in ordine di ordinal: è lo stesso ordine di partsSignal(), perché
  // _ensureCode$ li crea da lì. Caricati una volta sola per seduta.
  private _participantIds$(interviewId: string): Observable<string[]> {
    if (null !== this._participantIds) {
      return of(this._participantIds);
    }
    return this._participant.listForInterview(interviewId).pipe(
      map((rows) => rows.map((r) => r.id)),
      tap((ids) => {
        if (0 < ids.length) {
          this._participantIds = ids;
        }
      }),
    );
  }

  private _answerRows(
    interviewId: string,
    participantIds: readonly string[],
    ids: Record<string, string>,
  ): IAnswerRow[] {
    const qs = this.questionsSignal();
    const rows: IAnswerRow[] = [];
    this.partsSignal().forEach((part, pi) => {
      const pid = participantIds[pi];
      // Rispondenti non ancora seminati su DB (o aggiunti dopo la semina): si scrive al prossimo
      // giro, mai una riga con una FK inventata.
      if (undefined === pid) {
        return;
      }
      qs.forEach((q, qi) => {
        const ans = part.answers[qi];
        if (undefined === ans) {
          return;
        }
        const qid = ids[q.code];
        if (undefined !== qid) {
          rows.push({
            fk_interview_app_id: interviewId,
            fk_participant_app_id: pid,
            fk_question_app_id: qid,
            answer: ans.transcript,
            extra: null,
          });
        }
        (q.subCodes ?? []).forEach((code, si) => {
          const sid = ids[code];
          const sub = ans.subAnswers?.[si];
          if (undefined === sid || undefined === sub) {
            return;
          }
          const choices = sub.visualChoices;
          rows.push({
            fk_interview_app_id: interviewId,
            fk_participant_app_id: pid,
            fk_question_app_id: sid,
            answer: sub.transcript,
            // Voce visiva: le scelte Sì/No per immagine (chiave = uuid di question_image_app).
            extra: undefined !== choices && 0 < Object.keys(choices).length ? choices : null,
          });
        });
      });
    });
    return rows;
  }

  // Persiste un output di Claude appena prodotto (interview_generation_app, migrazione 0030):
  // refresh-proof, mai ripagare una chiamata AI. Fire-and-forget come l'autosave: un errore qui
  // non deve fermare il flusso di generazione (al peggio si perde il resume, non la seduta).
  private _persistGeneration(
    fields: Partial<Pick<IGenerationState, 'fase_a_md' | 'cli_md' | 'arch_md'>>,
  ): void {
    const id = this.interviewIdSignal();
    if (null === id) {
      return;
    }
    this._generation
      .save(id, fields)
      .pipe(
        catchError(() => of(undefined)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  // Upload once degli allegati sul bucket, prima di ogni snapshot: se path è già valorizzato non
  // fa nulla; se l'upload fallisce (offline) path resta null e si riprova al prossimo autosave.
  private _uploadAttachments$(): Observable<void> {
    const code = this.interviewCodeSignal();
    if (null === code) {
      return of(undefined);
    }
    const jobs: Observable<void>[] = [];
    const targets: [WritableSignal<IAttachment | null>, 'scheda_doc' | 'answers_file'][] = [
      [this.schedaDocSignal, 'scheda_doc'],
      [this.answersFileSignal, 'answers_file'],
    ];
    for (const [sig, kind] of targets) {
      const att = sig();
      const alreadyUploaded: boolean = undefined !== att?.path && null !== att?.path;
      if (null === att || '' === att.b64 || true === alreadyUploaded) {
        continue;
      }
      jobs.push(
        this._interview.uploadAttachment(code, att, kind).pipe(
          tap((path) => {
            if (null !== path) {
              sig.set({ ...att, path });
            }
          }),
          map(() => undefined),
        ),
      );
    }
    return 0 === jobs.length ? of(undefined) : forkJoin(jobs).pipe(map(() => undefined));
  }

  // Resume: lo snapshot porta solo il path → si ri-scarica dal bucket e si rimette il b64 in
  // memoria (serve per i blocchi del prompt Anthropic). Allegato senza path o download fallito:
  // resta con b64 vuoto (la generazione procede senza quel documento).
  public hydrateAttachments$(snap: IAnswersSnapshot): Observable<IAnswersSnapshot> {
    const jobs: Observable<void>[] = [];
    for (const key of ['schedaDoc', 'answersFile'] as const) {
      const att = snap[key];
      const path = att?.path;
      if (null === att || undefined === path || null === path || '' !== att.b64) {
        continue;
      }
      jobs.push(
        this._interview.downloadAttachmentB64(path).pipe(
          tap((b64) => {
            if (null !== b64) {
              snap[key] = { ...att, b64 };
            }
          }),
          map(() => undefined),
        ),
      );
    }
    return 0 === jobs.length ? of(snap) : forkJoin(jobs).pipe(map(() => snap));
  }

  // Riprende una sessione salvata (da /interviews): re-idrata i signals dallo snapshot e riparte
  // dalla schermata giusta. interviewId/code impostati dalla riga → _ensureCode$ non ricrea nulla
  // e il PDF rigenerato finisce sulla stessa pratica.
  // urlScreen: schermata presente nell'URL al momento del ripristino (solo refresh del browser,
  // v. _restoreFromUrl) — è l'unico caso in cui si torna direttamente su /profile. Il «Riprendi»
  // dall'archivio non la passa: si riparte SEMPRE dalle domande, così le risposte si possono
  // rivedere/correggere e rigenerare (i profili restano comunque idratati nei signals).
  resumeInterview(
    row: { id: string; code: number },
    snap: IAnswersSnapshot,
    gen: IGenerationState | null = null,
    urlScreen: Screen | null = null,
  ): void {
    this._pauseInterviewTiming();
    this._interviewTimer.start(snap.timing ?? null);
    this._epoch++;
    this._resumedSignal.set(true);
    // Pratica esistente: i participant_app sono già stati seminati alla prima sessione.
    this._participantsSeeded = true;
    this.sessionModeSignal.set(snap.sessionMode);
    this.schedaModeSignal.set(snap.schedaMode);
    this.percSignal.set(snap.percorso);
    this.archSignal.set(snap.arch);
    this.somministratoreSignal.set(snap.somministratore);
    this.dateSignal.set(snap.date);
    this.durSignal.set(snap.dur);
    this.personaIntervistataSignal.set(snap.personaIntervistata);
    this.partsSignal.set(snap.parts);
    this.schedaSignal.set(snap.scheda);
    this.schedaDocSignal.set(snap.schedaDoc);
    this.docNomiSignal.set(snap.docNomi);
    this.answersDocSignal.set(snap.answersDoc);
    this.answersFileSignal.set(snap.answersFile);
    this.activePSignal.set(0);
    this.activeQSignal.set(0);
    this.interviewIdSignal.set(row.id);
    this.interviewCodeSignal.set(row.code);
    this.pdfStateSignal.set('idle');
    this._visualBlocksCache = null;
    this.faseZeroTemiSignal.set([]);
    this.faseZeroEsitiSignal.set(null);
    this._faseZeroCtx = null;
    // Le domande sono quelle fotografate nello snapshot: le risposte sono allineate per indice a
    // QUELLE voci, non al set corrente. Snapshot pre-refactor (senza code): code derivato dall'id.
    if (0 < snap.questions.length) {
      this.questionsSignal.set(
        snap.questions.map((q) => ({ ...q, code: q.code ?? `Q${String(q.id).padStart(2, '0')}` })),
      );
    }
    // Percorso/perimetro fissati all'avvio (chip "Percorso" e blocco perimetro dei prompt): async,
    // non blocca. Le lookup servono a dare un nome agli impianti nel blocco perimetro.
    this.interviewStartSignal.set(null);
    this._lookupsSignal.set(null);
    this._questionIds = null;
    this._participantIds = null;
    this._questionSet
      .getInterviewStart(row.id)
      .pipe(
        catchError(() => of(null)),
        switchMap((start) =>
          forkJoin({
            start: of(start),
            lookups: this._questionSet.getLookups().pipe(catchError(() => of(null))),
            // Il set FISSATO serve per la mappa code → question_app.id delle risposte per riga:
            // lo snapshot porta le domande ma non gli id di DB.
            set:
              null === start
                ? of(null)
                : this._questionSet
                    .getQuestionSet(start.question_set_id)
                    .pipe(catchError(() => of(null))),
          }),
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(({ start, lookups, set }) => {
        this.interviewStartSignal.set(start);
        this._lookupsSignal.set(lookups);
        this._questionIds = null === set ? null : questionIdsByCode(set);
      });
    // Output di Claude persistiti (0030): ri-idratati così un refresh non ripaga le chiamate AI.
    if (null !== gen) {
      this.faseASignal.set(gen.fase_a_md ?? '');
      this.profMdSignal.set({ cliente: gen.cli_md ?? '', architetto: gen.arch_md ?? '' });
      // Job BE ancora in corso (tab chiusa a metà generazione): si torna sul processing e si
      // riattacca il polling — al done si arriva su /profile come se la tab non fosse mai morta.
      // Vale per ENTRAMBI i percorsi (refresh e «Riprendi»): ripartire dalle domande con un job
      // vivo produrrebbe solo un 409 al prossimo «Genera».
      if ('running' === gen.job_status) {
        this._go('processing');
        this.procProgSignal.set(0);
        this.procLabelSignal.set(
          'Costruendo il <strong>Profilo Architettonico</strong>…<br/><em style="font-size:14px">Generazione in corso sul server.</em>',
        );
        this.procDetailSignal.set('Profilo Architettonico · Fase B (ripresa)');
        this._pollArchJob();
        return;
      }
      // Direttamente su /profile SOLO se era la schermata nell'URL (refresh del browser lì
      // sopra) E l'Architettonico esiste. Il «Riprendi» dall'archivio riparte dalle domande.
      const archMd = gen.arch_md;
      if ('profile' === urlScreen && null !== archMd && '' !== archMd) {
        this._go('profile');
        return;
      }
    }
    this._go('document' === snap.sessionMode ? 'scheda' : 'interview');
  }

  // ── intervista ──
  setAns(field: keyof IAnswer, val: IAnswer[keyof IAnswer]): void {
    this._setAnsAt(this.activePSignal(), this.activeQSignal(), field, val);
  }

  // Le scritture asincrone (trascrizione Whisper, analisi paraverbale) devono atterrare sulla
  // domanda/partecipante attivi QUANDO L'OPERAZIONE È PARTITA, non quando la risposta arriva:
  // navigare durante l'attesa spostava la risposta sulla domanda sbagliata (es. Paure → Rassicurazioni).
  private _setAnsAt(pi: number, qi: number, field: keyof IAnswer, val: IAnswer[keyof IAnswer]): void {
    this.partsSignal.update((pp) =>
      pp.map((p, i) =>
        i !== pi ? p : { ...p, answers: p.answers.map((a, ai) => (ai !== qi ? a : { ...a, [field]: val })) },
      ),
    );
  }

  private _ansAt(pi: number, qi: number): IAnswer | undefined {
    return this.partsSignal()[pi]?.answers[qi];
  }

  // Posizione (partecipante, domanda) a cui agganciare letture/scritture di un'operazione async.
  private _pos(): { pi: number; qi: number } {
    return { pi: this.activePSignal(), qi: this.activeQSignal() };
  }

  // Pulsantiera non verbale (§1/§2 doc): un tap = un evento, solo mentre si registra la
  // risposta ("main"), per poter correlare il timestamp al segmento della trascrizione.
  pressChip(buttonId: string, note?: string): void {
    if ('main' !== this.recTargetSignal()) {
      return;
    }
    navigator.vibrate?.(15);
    const { pi, qi } = this._recPos ?? this._pos();
    const color = SYMBOLS.find((s) => s.s === buttonId)?.color ?? '#000';
    const topicId = this.questionsSignal()[qi]?.code ?? '';
    this._setAnsAt(pi, qi, 'chips', pushChipEvent(this._ansAt(pi, qi)?.chips ?? [], buttonId, color, topicId, note));
  }

  undoLastChip(): void {
    this.setAns('chips', undoLastChipEvent(this.ansSignal()?.chips ?? []));
  }

  chipCount(buttonId: string): number {
    return chipCounts(this.ansSignal()?.chips ?? [])[buttonId] ?? 0;
  }

  // ── domanda visiva (tavole): Sì/No per immagine; ricliccare la stessa scelta la annulla ──
  // Sì/No su un'immagine di una sottodomanda visiva (card D8, tavola D9): ricliccare toglie.
  setSubVisualChoice(idx: number, imageId: string, choice: 'si' | 'no'): void {
    const current = { ...(this.ansSignal()?.subAnswers?.[idx]?.visualChoices ?? {}) };
    if (current[imageId] === choice) {
      delete current[imageId];
    } else {
      current[imageId] = choice;
    }
    this.setSubAns(idx, 'visualChoices', current);
  }

  // ── spunti di approfondimento: sub-risposta per ogni spunto della domanda attiva ──
  // Nota: le domande possono essere ricaricate/modificate da Supabase dopo il seed iniziale di
  // partsSignal (vedi ConfigStore.loadRemote), quindi subAnswers può essere più corto del numero
  // di sondaggi correnti: si estende l'array anziché ignorare in silenzio l'indice mancante.
  setSubAns(idx: number, field: keyof ISubAnswer, val: SubAnsVal): void {
    const { pi, qi } = this._pos();
    this._setSubAnsAt(pi, qi, idx, field, val);
  }

  private _setSubAnsAt(
    pi: number,
    qi: number,
    idx: number,
    field: keyof ISubAnswer,
    val: SubAnsVal,
  ): void {
    this.partsSignal.update((pp) =>
      pp.map((p, i) =>
        i !== pi
          ? p
          : {
              ...p,
              answers: p.answers.map((a, ai) =>
                ai !== qi ? a : { ...a, subAnswers: this._patchSubAns(a.subAnswers, idx, field, val) },
              ),
            },
      ),
    );
  }

  private _patchSubAns(
    subAnswers: ISubAnswer[] | undefined,
    idx: number,
    field: keyof ISubAnswer,
    val: SubAnsVal,
  ): ISubAnswer[] {
    const arr = subAnswers ? [...subAnswers] : [];
    while (arr.length <= idx) {
      arr.push({ transcript: '', chips: [], analyzed: false });
    }
    arr[idx] = { ...arr[idx], [field]: val };
    return arr;
  }

  pressSubChip(idx: number, buttonId: string, note?: string): void {
    if (idx !== this.recTargetSignal()) {
      return;
    }
    navigator.vibrate?.(15);
    const { pi, qi } = this._recPos ?? this._pos();
    const color = SYMBOLS.find((s) => s.s === buttonId)?.color ?? '#000';
    const q = this.questionsSignal()[qi];
    const topicId = q?.subCodes?.[idx] ?? `${q?.code ?? ''}.${idx + 1}`;
    const chips = this._ansAt(pi, qi)?.subAnswers?.[idx]?.chips ?? [];
    this._setSubAnsAt(pi, qi, idx, 'chips', pushChipEvent(chips, buttonId, color, topicId, note));
  }

  undoLastSubChip(idx: number): void {
    const chips = this.ansSignal()?.subAnswers?.[idx]?.chips ?? [];
    this.setSubAns(idx, 'chips', undoLastChipEvent(chips));
  }

  subChipCount(idx: number, buttonId: string): number {
    return chipCounts(this.ansSignal()?.subAnswers?.[idx]?.chips ?? [])[buttonId] ?? 0;
  }

  addParticipant(): void {
    this.partsSignal.update((pp) => [
      ...pp,
      { name: `Partecipante ${pp.length + 1}`, email: '', answers: mkAns(this.questionsSignal()) },
    ]);
  }

  renameParticipant(i: number, name: string): void {
    this.partsSignal.update((pp) => pp.map((p, pi) => (pi !== i ? p : { ...p, name })));
  }

  // Email del rispondente per l'invio dei link di misura successivi (T1p/T1/T2, §3 doc DEV_v7).
  setParticipantEmail(i: number, email: string): void {
    this.partsSignal.update((pp) => pp.map((p, pi) => (pi !== i ? p : { ...p, email })));
  }

  consentoDraftFor(i: number): IConsentoDraft {
    return this.consentoDraftSignal()[i] ?? { metodo: 'verbale', onlineRiferimento: '' };
  }

  hasConsentoDraft(i: number): boolean {
    return undefined !== this.consentoDraftSignal()[i];
  }

  // Checkbox esplicita "consenso raccolto": senza un'azione affermativa non si registra niente
  // (un <select> lasciato sul valore di default non genera un change event, quindi da solo non
  // basterebbe a distinguere "raccolto verbalmente" da "non ancora toccato").
  setConsentoAcquisito(i: number, acquisito: boolean): void {
    this.consentoDraftSignal.update((d) => {
      if (!acquisito) {
        const { [i]: _drop, ...rest } = d;
        return rest;
      }
      return { ...d, [i]: d[i] ?? { metodo: 'verbale', onlineRiferimento: '' } };
    });
  }

  setConsentoMetodo(i: number, metodo: ConsentoMetodo): void {
    const draft = this.consentoDraftFor(i);
    this.consentoDraftSignal.update((d) => ({ ...d, [i]: { ...draft, metodo } }));
  }

  setConsentoOnlineRiferimento(i: number, onlineRiferimento: string): void {
    const draft = this.consentoDraftFor(i);
    this.consentoDraftSignal.update((d) => ({ ...d, [i]: { ...draft, onlineRiferimento } }));
  }

  // ── registrazione vocale → Whisper (risposta principale) ──
  startRecording(): void {
    this._startRecording('main');
  }

  stopRecording(): void {
    this._stopRecording('main');
  }

  // ── analisi paraverbale AI sulla singola risposta (risposta principale) ──
  analyzeAI(): void {
    this._analyzeAI('main');
  }

  // ── stessa pipeline registrazione/AI, per la sub-risposta di uno spunto ──
  isSubRecSignal(idx: number): boolean {
    return this.recTargetSignal() === idx;
  }

  isSubTranscribingSignal(idx: number): boolean {
    return this.transcribingTargetSignal() === idx;
  }

  isSubAISignal(idx: number): boolean {
    return this.aiTargetSignal() === idx;
  }

  // true se un'altra registrazione/trascrizione è in corso (il recorder è unico e condiviso)
  isBusyElsewhere(target: 'main' | number): boolean {
    const rec = this.recTargetSignal();
    const tr = this.transcribingTargetSignal();
    return (null !== rec && rec !== target) || (null !== tr && tr !== target);
  }

  startSubRecording(idx: number): void {
    this._startRecording(idx);
  }

  stopSubRecording(idx: number): void {
    this._stopRecording(idx);
  }

  analyzeSubAI(idx: number): void {
    this._analyzeAI(idx);
  }

  private _getTranscript(pi: number, qi: number, target: 'main' | number): string {
    const ans = this._ansAt(pi, qi);
    return 'main' === target ? (ans?.transcript ?? '') : (ans?.subAnswers?.[target]?.transcript ?? '');
  }

  private _setTranscript(pi: number, qi: number, target: 'main' | number, val: string): void {
    if ('main' === target) {
      this._setAnsAt(pi, qi, 'transcript', val);
    } else {
      this._setSubAnsAt(pi, qi, target, 'transcript', val);
    }
  }

  private _setAnalyzed(pi: number, qi: number, target: 'main' | number): void {
    if ('main' === target) {
      this._setAnsAt(pi, qi, 'analyzed', true);
    } else {
      this._setSubAnsAt(pi, qi, target, 'analyzed', true);
    }
  }

  private _startRecording(target: 'main' | number): void {
    from(this._recorder.start())
      .pipe(
        tap(() => {
          this.recTargetSignal.set(target);
          this._recStartAt = Date.now();
          this._recPos = this._pos();
        }),
        catchError((err) => {
          this.recTargetSignal.set(null);
          this._error(this._msg(err));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  private _getChips(pi: number, qi: number, target: 'main' | number): IChipEvent[] {
    const ans = this._ansAt(pi, qi);
    return 'main' === target ? (ans?.chips ?? []) : (ans?.subAnswers?.[target]?.chips ?? []);
  }

  private _stopRecording(target: 'main' | number): void {
    this.recTargetSignal.set(null);
    this.transcribingTargetSignal.set(target);
    const recStartAt = this._recStartAt;
    this._recStartAt = null;
    const { pi, qi } = this._recPos ?? this._pos();
    this._recPos = null;
    const epoch = this._epoch;
    from(this._recorder.stop())
      .pipe(
        switchMap((rec) =>
          rec ? this._api.transcribe(rec.blob, rec.ext) : of<ITranscription>({ text: '', segments: [] }),
        ),
        tap(({ text, segments }) => {
          if (!text || epoch !== this._epoch) {
            return;
          }
          if (segments.length && null !== recStartAt) {
            const events = this._getChips(pi, qi, target).filter((e) => !e.undone && new Date(e.t).getTime() >= recStartAt);
            text = insertInlineMarkers(segments, events, recStartAt);
          } else if (this._getChips(pi, qi, target).some((e) => !e.undone)) {
            this._warn('Trascrizione senza timestamp: le annotazioni non sono state collocate nel testo.');
          }
          const prev = this._getTranscript(pi, qi, target).trim();
          this._setTranscript(pi, qi, target, prev ? prev + ' ' + text : text);
          this.persistAnswers();
        }),
        catchError((err) => {
          this._error('Errore trascrizione: ' + this._msg(err));
          return EMPTY;
        }),
        finalize(() => this.transcribingTargetSignal.set(null)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  private _analyzeAI(target: 'main' | number): void {
    const { pi, qi } = this._pos();
    const transcript = this._getTranscript(pi, qi, target).trim();
    if (!transcript) {
      return;
    }
    this.aiTargetSignal.set(target);
    const q = this.questionsSignal()[qi];
    const domanda =
      'main' === target ? `Domanda: "${q.text}"` : `Domanda: "${q.text}"\nSpunto: "${q.sondaggi[target]}"`;
    const epoch = this._epoch;
    this._api
      .generate(this.promptsSignal().paraverbal, `${domanda}\nRisposta: ${transcript}`, 1000)
      .pipe(
        tap((out) => {
          if (out && epoch === this._epoch) {
            this._setTranscript(pi, qi, target, out);
            this._setAnalyzed(pi, qi, target);
            this.persistAnswers();
          }
        }),
        catchError(() => {
          this._error('Errore di connessione al servizio.');
          return EMPTY;
        }),
        finalize(() => this.aiTargetSignal.set(null)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  // ── trascrizione / scheda (testo) ──
  buildTranscript(): string {
    return buildTranscript({
      sessionMode: this.sessionModeSignal(),
      arch: this.archSignal(),
      somministratore: this.somministratoreSignal(),
      date: this.dateSignal(),
      perc: this.percSignal(),
      dur: this.durSignal(),
      parts: this.partsSignal(),
      questions: this.questionsSignal(),
      answersDoc: this.answersDocSignal(),
      answersFile: this.answersFileSignal(),
    });
  }

  // ── avvio generazione: (merge se 2ª trascrizione) → Fase A ──
  public startGeneration(): void {
    // su errore si torna esattamente da dove si è avviata la generazione (es. scheda upload),
    // non al sourceScreen generico.
    const origin: string = this._router.url;
    const plan: { tr$: Observable<string>; nomi: string[] } | null =
      'document' === this.sessionModeSignal() ? this._documentPlan() : this._interviewPlan();
    if (null === plan) {
      return;
    }
    this._interviewTimer.finish();
    this._saveTimingCheckpoint();
    // Snapshot risposte prima del lancio: se l'AI o il PDF falliscono, da /interviews si riprende.
    this.persistAnswers();
    this._launchGeneration(plan.tr$, plan.nomi, origin);
  }

  // modalità documento: testo incollato e/o file allegato. null se manca tutto (warn già mostrato).
  private _documentPlan(): { tr$: Observable<string>; nomi: string[] } | null {
    const hasText: boolean = this.answersDocSignal().trim().length > 0;
    const file = this.answersFileSignal();
    if (false === hasText && null === file) {
      this._warn('Incolla le risposte o allega un documento prima di generare.');
      return null;
    }
    const names: string[] = this.docNomiSignal()
      .split(/\s*,\s*|\s+e\s+/i)
      .map((s) => s.trim())
      .filter((s) => '' !== s);
    // Campo nominativi vuoto → si tengono i nomi già in partsSignal (es. quello di /intro),
    // il cui default è comunque 'Cliente' se non è mai stato toccato nulla.
    const finalNames: string[] = names.length > 0 ? names : this.partsSignal().map((p) => p.name);
    this.partsSignal.set(finalNames.map((n) => ({ name: n, answers: mkAns(this.questionsSignal()) })));
    const head: string = `PROFILO ABITATIVO — RISPOSTE CARICATE — CASÂBITO®\nArchitetto: ${this.archSignal() || '—'} | Somministratore: ${this.somministratoreSignal() || '—'} | Data: ${this.dateSignal() || '—'} | Percorso: ${this.percSignal() || '—'}\nPartecipanti: ${finalNames.join(', ')}\n\n`;
    // Il PDF/immagine allegato arriva all'AI come blocco a parte (_attachmentBlocks): qui basta
    // un rimando. Il Word (extractedText) non ha un blocco, quindi il suo testo va nel body —
    // insieme al testo digitato, se presente entrambi (niente si perde).
    const parts: string[] = [];
    if (hasText) {
      parts.push(this.answersDocSignal().trim());
    }
    if (undefined !== file?.extractedText) {
      parts.push(file.extractedText);
    } else if (null !== file && false === hasText) {
      parts.push(`(Le risposte del cliente sono fornite nel documento allegato: ${file.name})`);
    }
    return { tr$: of(head + parts.join('\n\n')), nomi: finalNames };
  }

  // modalità intervista: trascrizione conduttore, eventualmente unita a quella dell'assistente.
  private _interviewPlan(): { tr$: Observable<string>; nomi: string[] } {
    const conduttore: string = this.buildTranscript();
    const asst: string = this.assistantTranscriptSignal().trim();
    const nomi: string[] = this.partsSignal().map((p) => p.name);
    const tr$: Observable<string> =
      asst.length > 0
        ? defer(() => {
            this._go('processing');
            this.procProgSignal.set(10);
            this.procLabelSignal.set(
              'Unifico le <strong>due trascrizioni</strong>…<br/><em style="font-size:14px">Conduttore e Assistente.</em>',
            );
            this.procDetailSignal.set('Merge trascrizioni');
            return this._api.generate(
              this.promptsSignal().merge,
              `[TRASCRIZIONE 1 — CONDUTTORE]\n${conduttore}\n\n[TRASCRIZIONE 2 — ASSISTENTE]\n${asst}`,
              8000,
            );
          })
        : of(conduttore);
    return { tr$, nomi };
  }

  private _launchGeneration(tr$: Observable<string>, nomi: string[], origin: string): void {
    tr$
      .pipe(
        tap((tr) => this._mergedTranscriptSignal.set(tr)),
        switchMap((tr) => this._runFaseA$(tr, nomi)),
        catchError((err) => {
          this._router.navigateByUrl(origin);
          this._error('Errore durante la generazione: ' + this._msg(err));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  // Registrazione vocale generica per campi di testo libero fuori dal flusso domande/risposte
  // (es. "Parole del cliente" / Rettifiche in Rating, §3 doc DEV_v6): stesso RecorderService +
  // ApiService.transcribe della trascrizione dell'intervista, senza chip/topic da agganciare.
  startFreeRecording$(): Observable<void> {
    return from(this._recorder.start()).pipe(
      catchError((err) => {
        this._error(this._msg(err));
        return EMPTY;
      }),
    );
  }

  stopFreeRecording$(): Observable<string> {
    return from(this._recorder.stop()).pipe(
      switchMap((rec) =>
        rec ? this._api.transcribe(rec.blob, rec.ext) : of<ITranscription>({ text: '', segments: [] }),
      ),
      map(({ text }) => text),
      catchError((err) => {
        this._error('Errore trascrizione: ' + this._msg(err));
        return of('');
      }),
    );
  }

  // Risposta del cliente al chiarimento posto per un'incongruenza (§2 doc DEV_v6): entra nella
  // generazione dei documenti in Fase B, insieme alle ipotesi confermate.
  setIncongruenzaRisposta(i: number, val: string): void {
    this.incongruenzeSignal.update((list) => list.map((c, ci) => (ci !== i ? c : { ...c, rispostaCliente: val })));
  }

  // Riapre la Ponderazione da /profile (es. per correggere pesi e rigenerare SOLO il Profilo
  // Architettonico, v. generateProfiloArchitettonico): rilegge l'Indice dal Profilo Abitativo già
  // scritto, che non cambia. Nel flusso normale (Nota §2) la stessa estrazione avviene già dentro
  // runFaseB, prima ancora di arrivare qui.
  // Riapre la Ponderazione da /profile senza cancellarla se è già stata compilata: si correggono
  // i pesi, non si riparte da capo. Ri-semina dal Profilo Abitativo solo se non c'è ancora nulla
  // da mostrare.
  seedPonderazione(): void {
    if (null !== this.ponderazioneSignal()) {
      this._go('ponderazione');
      return;
    }
    this._go('processing');
    this.procProgSignal.set(30);
    this.procLabelSignal.set(
      'Preparo la <strong>Ponderazione</strong>…<br/><em style="font-size:14px">Rileggo gli elementi del Profilo Abitativo.</em>',
    );
    this.procDetailSignal.set('Ponderazione');
    this._seedPonderazioneFrom$(this.profMdSignal().cliente)
      .pipe(
        tap(() => {
          this.procProgSignal.set(100);
          this._go('ponderazione');
        }),
        catchError((err) => {
          this._go('profile');
          this._error('Errore: ' + this._msg(err));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  setPeso(codice: string, points: number): void {
    this.ponderazioneSignal.update((p) => {
      if (null === p) {
        return p;
      }
      const items = p.items.map((i) => (i.codice === codice ? { ...i, finalPoints: points } : i));
      return { ...p, items, total: items.reduce((s, i) => s + i.finalPoints, 0) };
    });
  }

  setPonderazioneFilledBy(name: string): void {
    this.ponderazioneSignal.update((p) => (null === p ? p : { ...p, filledBy: name }));
  }

  // "Ricomincia" riporta ai valori proposti: nessun pulsante dedicato "Confermo la proposta" (v.
  // Correzione) --- è "Avanti" a confermare qualunque valore ci sia al momento, il vincolo è solo
  // il totale a 100 (Nota §3.2). confirmedAsProposed si deduce da sé in confirmPonderazione.
  restartPonderazione(): void {
    this.ponderazioneSignal.update((p) => {
      if (null === p) {
        return p;
      }
      const items = p.items.map((i) => ({ ...i, finalPoints: i.proposedPoints }));
      return { ...p, items, total: items.reduce((s, i) => s + i.finalPoints, 0) };
    });
  }

  // Ponderazione saltata (Nota §3.2). Non naviga: la schermata chiama poi
  // generateProfiloArchitettonico (la scelta di profondità post-ponderazione non esiste più,
  // v. 21_fe_tre_profondita.tex: il percorso si sceglie PRIMA dell'intervista).
  skipPonderazione(): void {
    this.ponderazioneSignal.update((p) => (null === p ? p : { ...p, skipped: true }));
  }

  // Chiude la schermata pesi (calcola gli scostamenti, v. _deviationFlags). Non naviga.
  // confirmedAsProposed (Nota §3.2, "il cliente può confermare la proposta senza modifiche: in
  // tal caso il sistema lo annota") si deduce qui, non da un pulsante dedicato: se a questo punto
  // ogni valore è ancora uguale alla proposta, il cliente non ha mai toccato nulla.
  confirmPonderazione(): void {
    this.ponderazioneSignal.update((p) => {
      if (null === p) {
        return p;
      }
      return {
        ...p,
        confirmedAsProposed: p.items.every((i) => i.finalPoints === i.proposedPoints),
        deviationFlags: this._deviationFlags(p.items),
        confirmedAt: new Date().toISOString(),
      };
    });
  }

  // "Avvia intervista" dalla schermata percorso (/profondita, Tre Profondità blocco C): una
  // chiamata BE fissa versione del questionario, percorso proposto/scelto e perimetro; poi si
  // entra nelle domande. 409 = già avviata (refresh o Riprendi su una pratica partita): si
  // prosegue senza riscrivere nulla, il percorso è quello fissato la prima volta.
  startInterview(body: IStartInterviewBody): void {
    if (this.startingSignal()) {
      return;
    }
    this.startingSignal.set(true);
    this._ensureCode$()
      .pipe(
        switchMap(() => {
          const id = this.interviewIdSignal();
          if (null === id) {
            throw new Error('Pratica non ancora creata: riprova tra qualche secondo.');
          }
          return this._api.startInterview(id, body).pipe(
            catchError((err: unknown) => {
              if (409 === (err as { status?: number }).status) {
                this._warn('Intervista già avviata: il percorso resta quello fissato al primo avvio.');
                return of(null);
              }
              throw err;
            }),
          );
        }),
        switchMap((res) => {
          if (null !== res) {
            return of<IInterviewStart>({
              question_set_id: res.question_set_id,
              percorso_proposto: body.percorso_proposto,
              percorso_scelto: body.percorso_scelto,
              motivazione: body.motivazione ?? null,
              ambienti: body.ambienti,
              impianti: body.impianti,
            });
          }
          // 409: già avviata. Domande e risposte sono quelle già in memoria (o ri-idratate dallo
          // snapshot alla ripresa): NON si riapplica il set, azzererebbe le risposte date.
          return of(null);
        }),
        switchMap((start) => this._applyInterviewStart$(start)),
        tap(() => this._go('interview')),
        catchError((err: unknown) => {
          const detail = (err as { error?: { message?: string } }).error?.message ?? this._msg(err);
          this._error('Avvio intervista non riuscito: ' + detail);
          return EMPTY;
        }),
        finalize(() => this.startingSignal.set(false)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  regenFaseA(): void {
    const nomi = this.partsSignal().map((p) => p.name);
    this._runFaseA$(this._mergedTranscriptSignal() || this.buildTranscript(), nomi)
      .pipe(
        catchError((err) => {
          this._go('validation');
          this._error('Errore: ' + this._msg(err));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  // ── Fase B, stadio 1: genera SOLO il Profilo Abitativo, poi passa a Ponderazione/Profondità
  // (Nota_Sviluppo_v3 §2: stati 6-7 vengono PRIMA del Profilo Architettonico — stato 8 — non
  // generati insieme. V. Correzione in 06_profilo_architettonico.tex). ──
  runFaseB(validato: string, note: string): void {
    this._faseZeroCtx = { validato, note };
    this.faseZeroTemiSignal.set([]);
    this.faseZeroEsitiSignal.set(null);
    this._runProfiloAbitativo(false);
  }

  // ── Fase Zero: azioni della pulsantiera di chiarimento (Specifica §3) ──
  setFaseZeroRisposta(i: number, val: string): void {
    this.faseZeroTemiSignal.update((tt) =>
      tt.map((t, idx) => (idx !== i ? t : { ...t, risposta: val, timestampRegistrazione: new Date().toISOString() })),
    );
  }

  // [Chiarito] — conferma rapida quando la risposta è inequivocabile: salta la rivalutazione del
  // sistema. Ammessa solo con una risposta registrata (il contenuto verbale entra SEMPRE nel
  // profilo, Specifica §1 principio 2).
  markFaseZeroChiarito(i: number): void {
    this.faseZeroTemiSignal.update((tt) =>
      tt.map((t, idx) =>
        idx !== i || '' === t.risposta.trim()
          ? t
          : {
              ...t,
              confermatoRapido: true,
              origineNonChiarito: null,
              stato: 'da_chiarire' as const,
              timestampRegistrazione: new Date().toISOString(),
            },
      ),
    );
  }

  // [Non ponibile] — la mancata risposta è un esito, non un blocco (Rev. 9.3): il tema diventa
  // «non chiarito» senza risposta e la generazione si sblocca comunque.
  markFaseZeroNonPonibile(i: number): void {
    this.faseZeroTemiSignal.update((tt) =>
      tt.map((t, idx) =>
        idx !== i
          ? t
          : {
              ...t,
              stato: 'non_chiarito' as const,
              origineNonChiarito: 'non_ponibile' as const,
              confermatoRapido: false,
              timestampRegistrazione: new Date().toISOString(),
            },
      ),
    );
  }

  // Annulla [Chiarito]/[Non ponibile] premuto per errore, PRIMA della seconda passata.
  resetFaseZeroTema(i: number): void {
    this.faseZeroTemiSignal.update((tt) =>
      tt.map((t, idx) =>
        idx !== i ? t : { ...t, stato: 'da_chiarire' as const, origineNonChiarito: null, confermatoRapido: false },
      ),
    );
  }

  // Seconda (e ultima) passata: rivaluta i temi con le risposte e genera il profilo.
  submitChiarimenti(): void {
    if (!this.faseZeroReadySignal()) {
      this._error('Registra una risposta o segna «Non ponibile» per ogni tema.');
      return;
    }
    if (null === this._faseZeroCtx) {
      this._error('Sessione di generazione non trovata: riparti dalla validazione.');
      this._go('validation');
      return;
    }
    this._runProfiloAbitativo(true);
  }

  // Corpo comune delle due passate del Profilo Abitativo (prima passata = runFaseB, seconda =
  // submitChiarimenti). NON passa da regenProfile: quella via chiederebbe di "aggiornare" un
  // profilo che non esiste ancora (v. Correzione 11 in 06_profilo_architettonico.tex).
  private _runProfiloAbitativo(secondPass: boolean): void {
    const { validato, note } = this._faseZeroCtx ?? { validato: '', note: '' };
    const nomi = this.partsSignal().map((p) => p.name);
    const ctx = this._buildCtx(this._mergedTranscriptSignal() || this.buildTranscript());
    const incongTxt = this._incongruenzeTxt();
    const faseZeroTxt = secondPass ? this._faseZeroRisposteTxt() : '';
    const istruzione = this.isVeloceSignal()
      ? `\n\n[ISTRUZIONE OPERATIVA — PERCORSO VELOCE]\nL'architetto ha validato e corretto la Fase A qui sopra. Intervista breve: la FASE ZERO del tuo prompt NON si applica, NON restituire domande di chiarimento né il blocco "Esiti dei chiarimenti". Genera ORA il Profilo Abitativo nella struttura indicata dal tuo prompt (serve solo come base interna del Profilo Architettonico), recependo le correzioni. Restituisci SOLO il markdown.`
      : secondPass
      ? `\n\n[ISTRUZIONE OPERATIVA]\nQuesta è la SECONDA E ULTIMA passata della Fase Zero: rivaluta SOLO i temi delle risposte di chiarimento qui sopra con le stesse regole, registra l'esito di ogni tema nel blocco "Esiti dei chiarimenti" e genera ORA il Profilo Abitativo completo nella struttura indicata dal tuo prompt. NON restituire nuove domande di chiarimento: un tema si chiarisce una volta sola. Restituisci SOLO il markdown.`
      : `\n\n[ISTRUZIONE OPERATIVA]\nL'architetto ha validato e corretto la Fase A qui sopra. Genera ORA il Profilo Abitativo nella struttura indicata dal tuo prompt, recependo le correzioni. Restituisci SOLO il markdown.${FASE_ZERO_FORMAT_INSTRUCTION}`;

    this._ensureVisualBlocks$()
      .pipe(
        switchMap((vb) => {
          this._go('processing');
          this.procProgSignal.set(0);
          // Veloce: l'Abitativo è solo interno, la scritta non lo nomina.
          if (this.isVeloceSignal()) {
            this.procLabelSignal.set(
              'Preparo la base del <strong>Profilo Architettonico</strong>…<br/><em style="font-size:14px">Rileggo le risposte del cliente.</em>',
            );
            this.procDetailSignal.set('Percorso Veloce · Fase B');
          } else {
            this.procLabelSignal.set(
              'Costruendo il <strong>Profilo Abitativo</strong>…<br/><em style="font-size:14px">La lettura di emozioni e bisogni.</em>',
            );
            this.procDetailSignal.set(
              secondPass ? 'Profilo Abitativo · Fase B (dopo chiarimenti)' : 'Profilo Abitativo · Fase B',
            );
          }
          const cliMsg = `${ctx}\n\n[VALIDAZIONE DELL'ARCHITETTO — Fase A confermata/corretta]\n${validato}${note?.trim() ? `\n\n[NOTE AGGIUNTIVE DELL'ARCHITETTO]\n${note.trim()}` : ''}${incongTxt}${faseZeroTxt}${istruzione}`;
          return this._api.generate(this._sysCli(nomi), this._userContent(cliMsg, vb), environment.archMaxTokens);
        }),
        switchMap((cliMd) => {
          const domande = this._parseFaseZeroDomande(cliMd);
          if (0 < domande.length) {
            if (this.isVeloceSignal()) {
              this._go('validation');
              this._error('Il sistema ha restituito domande di chiarimento, non previste nel percorso Veloce: riprova con «Conferma e genera».');
              return EMPTY;
            }
            if (secondPass) {
              // Regola di terminazione (Specifica §2): UN solo giro di chiarimento per tema.
              // eslint-disable-next-line no-console -- violazione del contratto di seconda passata
              console.error('[fase-zero] nuove domande in seconda passata — non ammesse', domande);
              this._go('chiarimenti');
              this._error(
                'Il sistema ha restituito nuove domande invece del profilo: non ammesse in seconda passata. Riprova con «Genera il profilo».',
              );
              return EMPTY;
            }
            this.faseZeroTemiSignal.set(domande);
            this.procProgSignal.set(100);
            this._go('chiarimenti');
            return EMPTY;
          }
          this.profMdSignal.update((p) => ({ ...p, cliente: cliMd }));
          this._persistGeneration({ cli_md: cliMd });
          this._warnIfBlankCliMd(cliMd);
          if (this.isVeloceSignal()) {
            // Veloce: niente Ponderazione, si passa subito all'Architettonico.
            this.generateProfiloArchitettonico();
            return EMPTY;
          }
          this._registraEsitiFaseZero(cliMd);
          this.procProgSignal.set(60);
          this.procLabelSignal.set(
            'Preparo la <strong>Ponderazione</strong>…<br/><em style="font-size:14px">Gli elementi da pesare.</em>',
          );
          this.procDetailSignal.set('Ponderazione');
          return this._seedPonderazioneFrom$(cliMd).pipe(
            tap(() => {
              this.procProgSignal.set(100);
              this._go('ponderazione');
            }),
          );
        }),
        catchError((err) => {
          this._go(secondPass ? 'chiarimenti' : 'validation');
          this._error('Errore durante la generazione: ' + this._msg(err));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  // Avvio fissato → domande attive: legge il set della pratica (versione fissata) e le lookup
  // (nomi per il blocco perimetro), proietta con activeQuestions e RESETTA le
  // risposte dei partecipanti sulla nuova lista (l'avvio precede qualunque risposta). Senza set
  // (null) non tocca nulla: si resta sui DEFAULT_QUESTIONS.
  private _applyInterviewStart$(start: IInterviewStart | null): Observable<void> {
    if (null === start) {
      return of(undefined);
    }
    this.interviewStartSignal.set(start);
    return forkJoin({
      set: this._questionSet.getQuestionSet(start.question_set_id),
      lookups: this._questionSet.getLookups(),
    }).pipe(
      map(({ set, lookups }) => {
        if (null === set) {
          throw new Error('Versione del questionario non trovata.');
        }
        this._lookupsSignal.set(lookups);
        this._questionIds = questionIdsByCode(set);
        const qs = activeQuestions(
          set,
          { percorso: start.percorso_scelto, ambienti: start.ambienti, impianti: start.impianti },
          (path) => this._tavoleSvc.publicUrl(path),
        ).map((q) =>
          // Tavole D9: ordine delle immagini mescolato UNA volta per seduta (finisce nello
          // snapshot con le domande, quindi sopravvive a refresh e ripresa). Card D8: ordine admin.
          isVisualQuestion(q) ? { ...q, subImages: q.subImages?.map((imgs) => (null === imgs ? null : this._shuffle(imgs))) } : q,
        );
        this.questionsSignal.set(qs);
        this.partsSignal.update((pp) => pp.map((p) => ({ ...p, answers: mkAns(qs) })));
        this.activeQSignal.set(0);
        this.persistAnswers();
      }),
    );
  }

  // ── Fase B, stadio 2: genera il Profilo Architettonico con i pesi REALI del cliente (mai una
  // stima, v. _pesiTxt) --- chiamata dalla schermata Ponderazione dopo la conferma (o lo skip),
  // o di nuovo da /profile per rigenerare SOLO l'Architettonico con una ponderazione corretta,
  // senza toccare il Profilo Abitativo già scritto. La profondità leggera/media/pesante NON viene
  // più dichiarata qui (schermata rimossa con le Tre Profondità): il Prompt 2 torna a stimarla;
  // percorso e perimetro dell'intervista gli arrivano con lo step 7 (plumbing dati). ──
  generateProfiloArchitettonico(): void {
    const veloce = this.isVeloceSignal();
    const ponderazione = this.ponderazioneSignal();
    // Stesso gate di Nota §2 ("stato 8 non raggiungibile se lo stato 6 non è concluso/saltato")
    // applicato QUI, non solo sul [disabled] del pulsante. Il Veloce non ha Ponderazione.
    if (false === veloce && (null === ponderazione || (false === ponderazione.skipped && 100 !== ponderazione.total))) {
      this._error('Completa la ponderazione prima di continuare.');
      return;
    }
    const nomi = this.partsSignal().map((p) => p.name);
    const ctx = this._buildCtx(this._mergedTranscriptSignal() || this.buildTranscript());
    // Sostituisce la sezione "Indice degli elementi (PA-§n)" del Profilo Abitativo con i pesi
    // finali REALI del cliente, SENZA un'altra chiamata AI per un'edit mirata: un LLM istruito a
    // "modifica solo questa sezione, lascia il resto identico" su un documento lungo è un compito
    // che spesso fallisce in silenzio (il modello riscrive la sezione quasi identica, ignorando i
    // valori nuovi) --- qui il testo lo componiamo noi, deterministico e sempre corretto, dato
    // che i valori esatti sono già in ponderazione.items. Se saltata, il cliente non ha scelto
    // nulla di reale da sostituire: si lascia la proposta dell'AI così com'è.
    const cliMd = null === ponderazione || ponderazione.skipped
      ? this.profMdSignal().cliente
      : this._finalizeCliMdIndice(this.profMdSignal().cliente, ponderazione);
    if (cliMd !== this.profMdSignal().cliente) {
      this.profMdSignal.update((p) => ({ ...p, cliente: cliMd }));
      this._persistGeneration({ cli_md: cliMd });
    }
    // Veloce: un solo blocco sostituisce pesi ed esiti (nessuno dei due esiste su quel percorso).
    const pesiTxt = veloce ? VELOCE_ARCH_INSTRUCTION : null === ponderazione ? '' : this._pesiTxt(ponderazione);
    const esitiTxt = veloce ? '' : this._esitiTxt();

    this._ensureVisualBlocks$()
      .pipe(
        switchMap((vb) => {
          this._go('processing');
          this.procProgSignal.set(0);
          this.procLabelSignal.set(
            'Costruendo il <strong>Profilo Architettonico</strong>…<br/><em style="font-size:14px">Le scelte progettuali.</em>',
          );
          this.procDetailSignal.set('Profilo Architettonico · Fase B');
          // Il layout non lo descrive più l'AI in prosa: viene composto dopo, su un template PDF
          // (v. shared/helpers/arch-pdf.ts). L'AI produce solo i contenuti.
          // Cruscotto e schede-ambiente girano in parallelo NEL BACKEND (job, v. sez. LaTeX
          // "generazione persistita"): il FE prepara i payload (li ha tutti in memoria qui),
          // avvia il job e fa polling su interview_generation_app — la tab può chiudersi,
          // le chiamate Claude finiscono comunque e l'esito resta su DB.
          const base = `[PROFILO ABITATIVO — GIÀ REDATTO, validato dal cliente]\n${cliMd}\n\n${ctx}\n\n[ISTRUZIONE OPERATIVA]\nGenera ORA il Profilo Architettonico completo traducendo il Profilo Abitativo qui sopra in scelte progettuali, seguendo tutti i Principi del tuo prompt.${pesiTxt}${esitiTxt}\n\n`;
          // La riga della pratica serve PRIMA di avviare il job (il BE scrive lo stato su
          // interview_generation_app con FK sull'intervista): _ensureCode$ la crea se manca.
          return this._ensureCode$().pipe(
            switchMap(() => {
              const id = this.interviewIdSignal();
              if (null === id) {
                throw new Error('Pratica non ancora creata: riprova tra qualche secondo.');
              }
              const esiti: IFaseZeroEsiti | null = this.faseZeroEsitiSignal();
              return this._api.startArchJob(id, {
                system: this._sysArch(nomi),
                blocks: this._promptBlocks(vb),
                cruscotto: {
                  text: base + ARCH_JSON_FORMAT_CRUSCOTTO,
                  maxTokens: environment.archMaxTokens,
                },
                ambienti: { text: base + ARCH_JSON_FORMAT_AMBIENTI, maxTokens: environment.archMaxTokens },
                // Per i PDF server-side: Abitativo finalizzato, intestazioni, tavole scelte e
                // testo della scheda (per {code}_abitativo.pdf e {code}_scheda.pdf).
                cliMd,
                esiti: null === esiti ? null : {
                  nCongruenti: esiti.nCongruenti,
                  nChiariti: esiti.nChiariti,
                  nNonChiariti: esiti.nNonChiariti,
                },
                meta: this._pdfMeta(),
                gallery: this._visualGallery(),
                schedaText: this._schedaTextForPdf(),
              });
            }),
          );
        }),
        tap(() => this._pollArchJob()),
        catchError((err) => {
          this._go(this._archRetryScreen());
          this._error('Errore durante la generazione: ' + this._msg(err));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  // ── rigenerazione da Valutazione (rettifiche + note, senza ripassare dalla Fase A) ──
  regenProfile(rettifiche: string, noteArch: string): void {
    const nomi = this.partsSignal().map((p) => p.name);
    const ctx = this._buildCtx(this._mergedTranscriptSignal() || this.buildTranscript());
    const ponderazione = this.ponderazioneSignal();
    const pesiTxt = null === ponderazione ? '' : this._pesiTxt(ponderazione);

    this._ensureVisualBlocks$()
      .pipe(
        switchMap((vb) => {
          this._go('processing');
          this.procProgSignal.set(0);
          this.procLabelSignal.set(
            'Aggiorno il <strong>Profilo Abitativo</strong>…<br/><em style="font-size:14px">Con le tue rettifiche.</em>',
          );
          this.procDetailSignal.set('Profilo Abitativo');
          const cliMsg = `PROFILO ABITATIVO DA AGGIORNARE:\n${this.profMdSignal().cliente || ''}\n\nRETTIFICHE:\n${rettifiche}\nNOTE ARCHITETTO:\n${noteArch}\n\n${ctx}\n\n[ISTRUZIONE OPERATIVA]\nAggiorna il Profilo Abitativo recependo le rettifiche. Restituisci SOLO il markdown completo aggiornato.`;
          return this._api
            .generate(this._sysCli(nomi), this._userContent(cliMsg, vb), environment.archMaxTokens)
            .pipe(map((cliMd) => ({ cliMd, vb })));
        }),
        switchMap(({ cliMd, vb }) => {
          this.procProgSignal.set(50);
          this.procLabelSignal.set(
            'Aggiorno il <strong>Profilo Architettonico</strong>…<br/><em style="font-size:14px">Le scelte progettuali.</em>',
          );
          this.procDetailSignal.set('Profilo Architettonico');
          const archMsg = `PROFILO ARCHITETTONICO DA AGGIORNARE (JSON):\n${this.profMdSignal().architetto || ''}\n\nRETTIFICHE:\n${rettifiche}\nNOTE ARCHITETTO:\n${noteArch}\n\n[PROFILO ABITATIVO — AGGIORNATO]\n${cliMd}\n\n${ctx}\n\n[ISTRUZIONE OPERATIVA]\nAggiorna il Profilo Architettonico recependo le rettifiche e allineandolo al Profilo Abitativo aggiornato, seguendo tutti i Principi del tuo prompt.\n\n${pesiTxt}${this._esitiTxt()}\n\n`;
          // Stessi formati e budget della prima generazione. Entrambe le metà ricevono
          // lo stesso profilo precedente, rettifiche e Abitativo aggiornato.
          return forkJoin({
            cruscotto: this._api.generate(
              this._sysArch(nomi), this._userContent(archMsg + ARCH_JSON_FORMAT_CRUSCOTTO, vb),
              environment.archMaxTokens,
            ),
            ambienti: this._api.generate(
              this._sysArch(nomi), this._userContent(archMsg + ARCH_JSON_FORMAT_AMBIENTI, vb),
              environment.archMaxTokens,
            ),
          }).pipe(map(({ cruscotto, ambienti }) => ({
            cliMd, archMd: this._mergeArchHalves(cruscotto, ambienti),
          })));
        }),
        tap(({ cliMd, archMd }) => {
          this.procProgSignal.set(100);
          if (false === this._quadroCoerente(archMd, 'rating')) {
            return;
          }
          this.profMdSignal.set({ architetto: archMd, cliente: cliMd });
          this._warnIfBlankCliMd(cliMd);
          if (null === this._parseArchProfilo(archMd)) {
            this._warn(
              'Il Profilo Architettonico non è arrivato in formato strutturato (risposta troppo lunga o malformata): mostro il testo grezzo.',
            );
          }
          this._go('profile');
          this.persistInterview();
          // I profili sono cambiati: prima l'upsert su DB (il BE legge cli_md/arch_md da lì),
          // POI la rigenerazione del PDF — in parallelo il BE leggerebbe i profili vecchi.
          const id = this.interviewIdSignal();
          if (null !== id) {
            this._generation
              .save(id, { cli_md: cliMd, arch_md: archMd })
              .pipe(
                takeUntilDestroyed(this._destroyRef),
              )
              .subscribe({
                next: () => this.regenerateServerPdf(),
                error: (err) => this._error('Salvataggio dei profili non riuscito: ' + this._msg(err)),
              });
          }
        }),
        catchError((err) => {
          this._go('rating');
          this._error('Errore durante la generazione: ' + this._msg(err));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  // ── archivio interviste: crea la riga (una sola volta) + genera/upload il PDF ──
  // Fire-and-forget: su errore mostra il toast e passa a 'error' senza bloccare la navigazione.
  // Salvataggi d'archivio NON-PDF (.doc Abitativo, scheda, ponderazione, fase zero): il PDF lo
  // genera e carica SOLO il backend (dentro il job, o on-demand via regenerateServerPdf) — il
  // motore browser html2pdf è stato rimosso. pdfStateSignal riflette lo stato del PDF server,
  // non questi salvataggi.
  persistInterview(): void {
    this._ensureCode$()
      .pipe(
        switchMap((code) =>
          null === code
            ? of(undefined)
            : forkJoin([
                this._uploadScheda$(code),
                this._persistPonderazione$(),
                this._persistFaseZero$(),
              ]).pipe(map(() => undefined)),
        ),
        catchError((err) => {
          this._error("Errore salvataggio documenti d'archivio: " + this._msg(err));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  // Rigenera il PDF sul server (nessuna chiamata Claude: cli_md/arch_md sono già su DB):
  // bottone «Rigenera PDF» su /profile e dopo la rigenerazione post-rating.
  regenerateServerPdf(): void {
    const id = this.interviewIdSignal();
    if (null === id) {
      return;
    }
    this.pdfStateSignal.set('saving');
    this._api
      .regenPdf(id, {
        meta: this._pdfMeta(),
        gallery: this._visualGallery(),
        schedaText: this._schedaTextForPdf(),
      })
      .pipe(
        tap(() => this.pdfStateSignal.set('saved')),
        catchError((err) => {
          this.pdfStateSignal.set('error');
          this._error('Rigenerazione PDF non riuscita: ' + this._msg(err));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  // Ponderazione (se raccolta, v. generateProfiloArchitettonico): stesso momento di persistenza
  // del PDF, perché è solo qui che l'intervista ha finalmente un id (_ensureCode$ è già risolto
  // quando questo viene chiamato). Silenziosamente no-op se manca uno dei due.
  private _persistPonderazione$(): Observable<void> {
    const interviewId = this.interviewIdSignal();
    const ponderazione = this.ponderazioneSignal();
    if (null === interviewId || null === ponderazione) {
      return of(undefined);
    }
    return this._ponderazione.save(interviewId, ponderazione);
  }

  // Esiti Fase Zero: stesso momento di persistenza della ponderazione (serve l'id intervista,
  // che esiste solo dopo _ensureCode$). Silenziosamente no-op se il meccanismo non è mai scattato
  // (faseZeroEsitiSignal null: prompt pre-9.3 o generazione mai arrivata in fondo). Lo stamp
  // della versione del prompt viene da ConfigStore (riga app_prompt attiva al load).
  private _persistFaseZero$(): Observable<void> {
    const interviewId = this.interviewIdSignal();
    const esiti = this.faseZeroEsitiSignal();
    if (null === interviewId || null === esiti) {
      return of(undefined);
    }
    return this._faseZero.save(interviewId, esiti, this._config.promptVersionIdSignal());
  }

  // Crea la riga interview_app + un participant_app per ogni partecipante della sessione
  // (stesso momento, stesso ordine di partsSignal()): i rispondenti diventano righe stabili,
  // compilabili nel tempo (IBAP T0/T1/T2, IIP-P/IIP-C) anche a sessione live ormai chiusa. Il guard su
  // interviewCodeSignal evita che questo si ripeta sulle chiamate successive (es. regenProfile()).
  private _ensureCode$(): Observable<number | null> {
    const epoch = this._epoch;
    return this._ensureRow$().pipe(
      switchMap((row) => {
        if (null === row || epoch !== this._epoch || this._participantsSeeded) {
          return of(row);
        }
        // Flag alzato PRIMA della chiamata: un solo tentativo — createMany non ha UNIQUE su
        // ordinal, un retry cieco duplicherebbe i rispondenti. Pratica senza partecipanti =
        // degradata ma unica; il backfill manuale esiste in /interviews.
        this._participantsSeeded = true;
        return this._participant
          .createMany(
            row.id,
            this.partsSignal().map((p) => ({ name: p.name, email: p.email?.trim() || null })),
            this.gruppoRicercaSignal(),
          )
          .pipe(
            switchMap(() => this._registerPendingConsensi$(row.id)),
            map(() => row),
            catchError(() => of(row)),
          );
      }),
      map((row) => (epoch === this._epoch ? (row?.code ?? null) : null)),
    );
  }

  // Riga intervista (id tecnico + codice parlante), creata una sola volta per seduta: la lancia
  // startSession, l'autosave e persistInterview la trovano già fatta (o in volo: cache condivisa
  // con shareReplay, mai due INSERT). Guard di epoca: se durante la create() l'utente cambia
  // sessione (es. Riprendi), la riga appena creata NON ruba l'identità alla sessione corrente.
  private _ensureRow$(): Observable<{ id: string; code: number } | null> {
    const id = this.interviewIdSignal();
    const code = this.interviewCodeSignal();
    if (null !== id && null !== code) {
      return of({ id, code });
    }
    if (null === this._rowCreate || this._rowCreate.epoch !== this._epoch) {
      const epoch = this._epoch;
      const row$ = this._interview
        .create({
          client_names: this.nomiSignal(),
          percorso: this.percSignal(),
          interview_date: this.dateSignal() || null,
        })
        .pipe(
          // Identità (id/code) salvata SUBITO dopo la create: se il seeding a valle fallisce,
          // la riga non va persa — prima id/code restavano null e il tick successivo creava
          // una SECONDA intervista (la prima restava orfana in archivio).
          tap((row) => {
            if (null === row || epoch !== this._epoch) {
              return;
            }
            this.interviewIdSignal.set(row.id);
            this.interviewCodeSignal.set(row.code);
            this._syncIdInUrl();
          }),
          map((row) => (epoch === this._epoch ? row : null)),
          catchError(() => of(null)),
          tap((row) => {
            // Create fallita o annullata: cache buttata, il prossimo tick riprova.
            if (null === row && this._rowCreate?.epoch === epoch) {
              this._rowCreate = null;
            }
          }),
          shareReplay(1),
        );
      this._rowCreate = { epoch, row$ };
    }
    return this._rowCreate.row$;
  }

  // Riflette l'id nel path (/interview/<id>) senza cambiare pagina: è quello che il ripristino
  // post-refresh legge (v. _restoreFromUrl). Attivo solo sulle schermate del flusso intervista
  // (da /scheda in poi): /intro, /interviews, /admin, survey restano puliti.
  private _syncIdInUrl(): void {
    const id = this.interviewIdSignal();
    if (null === id) {
      return;
    }
    const segments = this._router.parseUrl(this._router.url).root.children['primary']?.segments ?? [];
    const screen = segments[0]?.path ?? '';
    if (!FLOW_SCREENS.has(screen) || segments[1]?.path === id) {
      return;
    }
    void this._router.navigate(['/', flowPath(screen), id], { replaceUrl: true });
  }

  // Refresh del browser a metà seduta: l'id nel path (/interview/<id>) permette di ricaricare la
  // pratica dal DB, stesso meccanismo di «Riprendi» in /interviews (si riparte da /interview o
  // /scheda, come lì). App inizializza lo store dopo il ripristino dell'autenticazione e
  // solo in presenza di sessione (o nel fallback locale senza Supabase).
  // ponytail: pathname letto assumendo base href '/', com'è in tutti i deploy.
  private _restoreFromUrl(): void {
    const [, screen, id] = window.location.pathname.split('/');
    // 'interviews' con id = pratica singola (/interviews/<id>); senza id è l'archivio, si ignora.
    if ((!FLOW_SCREENS.has(screen ?? '') && 'interviews' !== screen) || !id || null !== this.interviewIdSignal()) {
      return;
    }
    forkJoin([
      this._interview.getCode(id),
      this._interview.getAnswers(id),
      // Output di Claude persistiti (0030): fallito il fetch, si riprende senza (come prima).
      this._generation.get(id).pipe(catchError(() => of(null))),
    ])
      .pipe(
        // Gli allegati nello snapshot hanno solo il path: b64 ri-scaricato dal bucket prima di re-idratare.
        switchMap(([code, snap, gen]) =>
          null === snap
            ? of([code, snap, gen] as [number | null, IAnswersSnapshot | null, IGenerationState | null])
            : this.hydrateAttachments$(snap).pipe(
                map(
                  (s) =>
                    [code, s, gen] as [number | null, IAnswersSnapshot | null, IGenerationState | null],
                ),
              ),
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: ([code, snap, gen]) => {
          if (null === code) {
            this._warn('Intervista del link non trovata.');
            return;
          }
          if (null === snap) {
            // Riga senza snapshot (refresh subito dopo l'avvio): si adotta solo l'identità,
            // così il prossimo autosave scrive qui invece di creare una seconda pratica.
            // ponytail: seeded=true nel dubbio — meglio il backfill manuale in /interviews
            // che rispondenti duplicati.
            this._participantsSeeded = true;
            this.interviewIdSignal.set(id);
            this.interviewCodeSignal.set(code);
            return;
          }
          // Il path del refresh dice DOVE l'utente stava (flowPath: 'interviews' nel path =
          // schermata 'interview'): resumeInterview lo usa per tornare su /profile solo in
          // quel caso — il «Riprendi» dall'archivio (interviews.ts) non lo passa apposta.
          const urlScreen = ('interviews' === screen ? 'interview' : screen) as Screen;
          this.resumeInterview({ id, code }, snap, gen, urlScreen);
        },
        error: () => this._warn("Ripristino dell'intervista dal link non riuscito."),
      });
  }

  // Registra in consent_app le bozze di consenso raccolte su /intro (indice = ordinal su
  // partsSignal()) non appena i participant_app corrispondenti esistono davvero — "registrato
  // insieme al resto" per l'utente: nessuna scrittura separata, stesso momento di _ensureCode$.
  private _registerPendingConsensi$(interviewId: string): Observable<void> {
    const drafts = this.consentoDraftSignal();
    if (0 === Object.keys(drafts).length) {
      return of(undefined);
    }
    return this._participant.listForInterview(interviewId).pipe(
      switchMap((rows) => {
        const calls = rows
          .filter((r) => undefined !== drafts[r.ordinal])
          .map((r) => {
            const d = drafts[r.ordinal];
            return this._consenso.register(
              r.id,
              interviewId,
              d.metodo,
              'online' === d.metodo ? d.onlineRiferimento.trim() || null : null,
            );
          });
        return 0 === calls.length ? of(undefined) : forkJoin(calls).pipe(map(() => undefined));
      }),
      tap(() => this.consentoDraftSignal.set({})),
    );
  }

  // Scheda dell'Abitazione nell'archivio: PDF caricato → allegato originale; form → .docx generato.
  // Profilo Abitativo da solo (.docx, stesso formato del download in /profile) → bucket, per il
  // download diretto da /interviews. Il PDF completo resta il documento fuso (pdf_path).
  // Solo la scheda CARICATA come file dall'utente resta un upload del FE (è il suo originale,
  // va archiviato così com'è). Abitativo e scheda-da-form sono ora PDF generati dal BE
  // (buildDocumentPdf, v. schedaText nel payload del job): niente più finti .doc dal browser.
  private _uploadScheda$(code: number): Observable<void> {
    const doc = this.schedaDocSignal();
    if ('upload' === this.schedaModeSignal() && doc) {
      const ext = doc.name.includes('.') ? doc.name.split('.').pop()! : 'pdf';
      return this._interview.uploadScheda(code, this._export.attachmentBlob(doc), ext);
    }
    return of(undefined);
  }

  // Testo della scheda per il PDF server-side: null quando la scheda è un file caricato
  // (in quel caso l'originale lo archivia _uploadScheda$ e il BE non deve generare nulla).
  private _schedaTextForPdf(): string | null {
    if ('upload' === this.schedaModeSignal() && null !== this.schedaDocSignal()) {
      return null;
    }
    return this.schedaText();
  }

  // Testo della Scheda compilata a mano, coi nomi di impianti e opzioni dalle lookup (caricate
  // all'avvio e alla ripresa; prima stampa i codici). Unico punto per prompt, PDF, download.
  schedaText(): string {
    return buildSchedaText(this.schedaSignal(), this.percSignal(), this._lookupsSignal()?.impianti ?? []);
  }

  private _pdfMeta(): IExportMeta {
    return {
      nomi: this.nomiSignal(),
      arch: this.archSignal(),
      date: this.dateSignal(),
      perc: this.percSignal(),
    };
  }

  // ── Fase A: estrae ipotesi e ancore, da confermare ──
  private _runFaseA$(tr: string, nomi: string[]): Observable<void> {
    return defer(() =>
      this._ensureVisualBlocks$().pipe(
        switchMap((vb) => {
          this._go('processing');
          this.procProgSignal.set(15);
          this.procLabelSignal.set(
            'Rilevo <strong>ipotesi e ancore</strong>…<br/><em style="font-size:14px">Fase A · validazione.</em>',
          );
          this.procDetailSignal.set('Lettura risposte e scheda');
          const ctx = this._buildCtx(tr);
          const msg = `${ctx}\n\n[ISTRUZIONE OPERATIVA]\nEsegui ORA soltanto la FASE A (validazione). NON generare il documento completo.\n\nPRIMA DI TUTTO, prima di qualsiasi altro testo, scrivi un array JSON (anche vuoto: []) delle incongruenze tra trascrizione verbale, marcatori paraverbali (★ intensificazione, ↓ deflazione, ⋯ pausa, ~ ambivalenza) e pulsanti non verbali annotati inline nel testo (es. [R1], [G4], [NOTA:"..."]) — confronta cosa dice il cliente con cosa segnala il corpo, e includi anche le contraddizioni dirette tra due risposte verbali distinte. Ogni elemento dell'array: {"domanda": "riferimento sintetico al tema/domanda", "segnale": "quale marcatore o quale altra risposta ha fatto emergere la divergenza e perché", "domandaChiarimento": "la domanda puntuale da porre al cliente per chiarire"}. NON includere il campo "rispostaCliente": la compila l'architetto dopo aver posto la domanda. Se non rilevi nessuna incongruenza scrivi comunque [].\n\nSubito dopo l'array JSON, su una riga a sé stante, scrivi esattamente il separatore ${FASE_A_INCONGRUENZE_MARKER} e SOLO DOPO, in markdown sintetico:\n- I bisogni, le ipotesi e le soluzioni che hai estratto, una per riga, ciascuno con la frase-ancora del cliente tra virgolette.\n- I punti in cui le risposte sono mute o ambigue.\n- I dati della Scheda mancanti o «da rilevare».\nChiudi invitando l'architetto a confermare, correggere o integrare prima della stesura dei documenti.`;
          return this._api.generate(this._sysArch(nomi), this._userContent(msg, vb), environment.archMaxTokens);
        }),
      ),
    ).pipe(
      tap((out) => {
        this.procProgSignal.set(100);
        const { ipotesi, incongruenze } = this._parseFaseA(out);
        this.faseASignal.set(ipotesi);
        this._persistGeneration({ fase_a_md: ipotesi });
        this.incongruenzeSignal.set(this.isVeloceSignal() ? [] : incongruenze);
        this._go('validation');
      }),
      map(() => undefined),
    );
  }

  // Separa il blocco JSON delle incongruenze dal markdown delle ipotesi (§2 doc DEV_v6). Il JSON
  // viene chiesto PRIMA del markdown apposta: se la risposta viene troncata per limite token, a
  // saltare è il markdown (già leggibile parzialmente), non le incongruenze (l'unico dato che
  // andrebbe perso in silenzio senza che l'architetto se ne accorga). Se l'AI non rispetta il
  // formato (marker assente o JSON non valido), non blocca il flusso: le incongruenze restano
  // vuote e la schermata di validazione mostra "Nessuna incongruenza rilevata".
  private _parseFaseA(raw: string): { ipotesi: string; incongruenze: IIncongruenza[] } {
    const idx = raw.indexOf(FASE_A_INCONGRUENZE_MARKER);
    if (-1 === idx) {
      return { ipotesi: raw.trim(), incongruenze: [] };
    }
    const ipotesi = raw.slice(idx + FASE_A_INCONGRUENZE_MARKER.length).trim();
    const jsonPart = raw
      .slice(0, idx)
      .trim()
      .replace(/^```[a-z]*\n?/i, '')
      .replace(/```$/, '')
      .trim();
    try {
      const parsed = JSON.parse(jsonPart) as {
        domanda: string;
        segnale: string;
        domandaChiarimento: string;
      }[];
      return {
        ipotesi,
        incongruenze: parsed.map((p) => ({ ...p, rispostaCliente: '' })),
      };
    } catch {
      return { ipotesi, incongruenze: [] };
    }
  }

  // Allegati attivi: Scheda PDF (modalità upload) + documento risposte (percorso documento).
  // La Scheda Word non genera un blocco: il suo testo estratto (extractedText) va già dentro
  // <dati_scheda> via _buildCtx, l'API Anthropic non ha un blocco "document" per il formato nativo.
  private _attachmentBlocks(): ContentBlock[] {
    const b: ContentBlock[] = [];
    const sd = this.schedaDocSignal();
    if (this.schedaModeSignal() === 'upload' && sd && 'application/pdf' === sd.mediaType) {
      b.push(fileToBlock(sd));
    }
    const af = this.answersFileSignal();
    if (this.sessionModeSignal() === 'document' && af && undefined === af.extractedText) {
      b.push(fileToBlock(af));
    }
    return b;
  }

  private _userContent(text: string, visualBlocks: ContentBlock[] = []): UserContent {
    const blocks = this._promptBlocks(visualBlocks);
    if (0 === blocks.length) {
      return text;
    }
    return [...blocks, { type: 'text', text }];
  }

  // Allegati + tavole come blocchi pronti per il prompt, con cache breakpoint sull'ultimo:
  // Anthropic mette in cache tutto il prefisso fino a qui (system + questi blocchi). Stessi
  // allegati/stesso ruolo (_sysArch/_sysCli) ricompaiono più volte nella stessa intervista
  // (Fase B: le 2 chiamate parallele, poi regenProfile) — dalla seconda in poi, quel prefisso
  // non viene riprocessato da zero. Usato sia da _userContent che dal payload del job BE.
  private _promptBlocks(visualBlocks: ContentBlock[]): ContentBlock[] {
    const atts = [...this._attachmentBlocks(), ...visualBlocks];
    if (0 === atts.length) {
      return [];
    }
    const lastCached: ContentBlock = { ...atts[atts.length - 1], cache_control: { type: 'ephemeral' } };
    return [...atts.slice(0, -1), lastCached];
  }

  // Polling sull'esito del job BE (interview_generation_app): usato all'avvio del job e al
  // resume di una pratica con job 'running'. L'epoch ferma il polling se nel frattempo
  // l'operatore ha iniziato un'altra pratica.
  private _pollArchJob(): void {
    const id = this.interviewIdSignal();
    if (null === id) {
      return;
    }
    const epoch = this._epoch;
    const startedAt = Date.now();
    // Primo check immediato (utile al resume: il job può essere già finito), poi ogni 5s.
    const sub = timer(0, ARCH_JOB_POLL_MS)
      .pipe(
        switchMap(() => this._generation.get(id).pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((gen) => {
        if (epoch !== this._epoch) {
          sub.unsubscribe();
          return;
        }
        if ('done' === gen?.job_status && null !== gen.arch_md && '' !== gen.arch_md) {
          sub.unsubscribe();
          // done = anche il PDF è già su Storage (lo carica il job): qui non va rigenerato.
          this._onArchReady(gen.arch_md, true);
          return;
        }
        if ('error' === gen?.job_status) {
          sub.unsubscribe();
          const archMd = gen.arch_md;
          if (null !== archMd && '' !== archMd) {
            // Le chiamate Claude sono riuscite (arch_md c'è), è fallito solo il PDF server-side:
            // profilo comunque visibile, stato PDF 'error' → bottone «Rigenera PDF» su /profile.
            this._warn('PDF non riuscito sul server: puoi rigenerarlo dal profilo.');
            this._onArchReady(archMd, false);
            return;
          }
          this._go(this._archRetryScreen());
          this._error('Errore durante la generazione: ' + (gen.job_error ?? 'sconosciuto'));
          return;
        }
        if (Date.now() - startedAt > ARCH_JOB_TIMEOUT_MS) {
          sub.unsubscribe();
          this._go(this._archRetryScreen());
          this._error('La generazione non ha risposto entro il tempo massimo: riprova.');
        }
      });
  }

  // Esito del job arrivato (stessa logica del vecchio tap post-forkJoin): validazione bloccante
  // del Quadro di affidabilità, poi profilo visibile e salvataggi d'archivio. arch_md è già su
  // DB (lo scrive il BE): niente _persistGeneration qui. pdfOk = il job ha caricato anche il
  // PDF; false = PDF fallito sul server → stato 'error' e bottone «Rigenera PDF» su /profile.
  // Da dove si riprova l'Architettonico fallito: la Ponderazione, o nel Veloce (che non ce l'ha) la
  // validazione, il cui «Conferma e genera» rifà Abitativo interno + Architettonico.
  private _archRetryScreen(): Screen {
    return this.isVeloceSignal() ? 'validation' : 'ponderazione';
  }

  private _onArchReady(archMd: string, pdfOk: boolean): void {
    this.procProgSignal.set(100);
    const arch: IArchProfilo | null = this._parseArchProfilo(archMd);
    const hasAmbienti: boolean = null !== arch && Array.isArray(arch.ambienti);
    if (false === hasAmbienti) {
      this.pdfStateSignal.set('error');
      this._go(this._archRetryScreen());
      this._error('Profilo Architettonico non valido: rigeneralo prima di creare il PDF.');
      return;
    }
    // Validazione incrociata bloccante (Specifica Rev. 11.2 §3): mai pubblicare un
    // documento col Quadro di affidabilità incoerente col dato applicativo.
    if (false === this._quadroCoerente(archMd, 'ponderazione')) {
      return;
    }
    this.profMdSignal.update((p) => ({ ...p, architetto: archMd }));
    this._go('profile');
    this.pdfStateSignal.set(true === pdfOk ? 'saved' : 'error');
    this.persistInterview();
  }

  // Immagini scelte (Sì/No) della domanda visiva → blocchi "image" reali per il prompt AI
  // (oggi solo il testo "TAVOLA A — meaning: Sì" raggiungeva l'AI). Cache per-seduta: ogni
  // chiamata di generazione (Fase A, poi le 2 di Fase B) riusa lo stesso array già scaricato.
  private _ensureVisualBlocks$(): Observable<ContentBlock[]> {
    if (null !== this._visualBlocksCache) {
      return of(this._visualBlocksCache);
    }
    const srcById = new Map(
      this.questionsSignal().flatMap((q) => (q.subImages ?? []).flatMap((imgs) => (imgs ?? []).map((i) => [i.id, i.src] as const))),
    );
    const srcs = new Set<string>();
    this.partsSignal().forEach((p) =>
      p.answers.forEach((a) =>
        (a.subAnswers ?? []).forEach((sa) =>
          Object.keys(sa.visualChoices ?? {}).forEach((id) => {
            const src = srcById.get(id);
            if (src) {
              srcs.add(src);
            }
          }),
        ),
      ),
    );
    return from(
      Promise.all(
        [...srcs].map(async (src): Promise<ContentBlock> => {
          const { data, mediaType } = await urlToB64(src);
          return { type: 'image', source: { type: 'base64', media_type: mediaType, data } };
        }),
      ),
    ).pipe(tap((blocks) => (this._visualBlocksCache = blocks)));
  }

  // Immagini scelte, per l'appendice "Tavole scelte" del PDF archiviato.
  private _visualGallery(): IVisualGalleryEntry[] {
    const qs = this.questionsSignal();
    const out: IVisualGalleryEntry[] = [];
    this.partsSignal().forEach((p) => {
      p.answers.forEach((ans, qi) => {
        const q = qs[qi];
        (ans.subAnswers ?? []).forEach((sa, si) => {
          const imgs = q?.subImages?.[si];
          const vc = sa.visualChoices;
          if (!imgs || !vc) {
            return;
          }
          imgs.forEach((img) => {
            const choice = vc[img.id];
            if (choice) {
              out.push({ participant: p.name, tavola: q.sondaggi[si] ?? q.tag, meaning: img.meaning, choice, src: img.src });
            }
          });
        });
      });
    });
    return out;
  }

  // Chiarimenti raccolti sul blocco INCONGRUENZE DA CHIARIRE: entrano in Fase B insieme alla
  // validazione (§2 doc DEV_v6). Vuoto se non ce n'erano o non sono state ancora risposte.
  private _incongruenzeTxt(): string {
    const answered = this.incongruenzeSignal().filter((c) => '' !== c.rispostaCliente.trim());
    if (0 === answered.length) {
      return '';
    }
    const righe = answered
      .map(
        (c, i) =>
          `${i + 1}. ${c.domanda}\nDomanda posta al cliente: ${c.domandaChiarimento}\nRisposta del cliente: ${c.rispostaCliente.trim()}`,
      )
      .join('\n\n');
    return `\n\n[CHIARIMENTI SULLE INCONGRUENZE — Fase A]\n${righe}`;
  }

  // ── Fase Zero: parsing e registrazione degli esiti (Prompt 1 Rev. 9.3 + Specifica §2/§4) ──

  // Distingue "domande di chiarimento" (JSON, v. FASE_ZERO_FORMAT_INSTRUCTION) dal markdown del
  // profilo. Fallback regex per il caso in cui il modello ignori il formato JSON e restituisca la
  // sezione testuale del prompt ("Tema: … / Domanda da porre ora al cliente: … / Perché la
  // chiediamo: …"): meglio un parse approssimativo che archiviare le domande come profilo.
  private _parseFaseZeroDomande(raw: string): IFaseZeroTema[] {
    const mk = (tema: string, domanda: string, perche: string, i: number): IFaseZeroTema => ({
      temaId: i + 1,
      titolo: tema.trim(),
      domanda: domanda.trim(),
      perche: perche.trim(),
      risposta: '',
      stato: 'da_chiarire',
      confermatoRapido: false,
      origineNonChiarito: null,
      timestampRegistrazione: null,
    });
    if (raw.includes('domandeChiarimento')) {
      const obj = this._extractJsonObject(raw, 'faseZeroDomande');
      const rows =
        (obj?.['domandeChiarimento'] as { tema?: string; domanda?: string; perche?: string }[] | undefined) ?? [];
      return rows
        .filter((r) => 'string' === typeof r.tema && 'string' === typeof r.domanda)
        .slice(0, 5)
        .map((r, i) => mk(r.tema as string, r.domanda as string, r.perche ?? '', i));
    }
    if (/Domande di chiarimento/i.test(raw) && /In attesa delle risposte/i.test(raw)) {
      // eslint-disable-next-line no-console -- il modello ha ignorato il formato JSON richiesto
      console.warn('[fase-zero] domande in formato testuale invece che JSON: parse di ripiego');
      const re = /Tema\s*:\s*([^\n]+)\n[\s\S]*?Domanda[^:\n]*:\s*([\s\S]*?)\s*Perch[eé][^:\n]*:\s*([^\n]+)/gi;
      const out: IFaseZeroTema[] = [];
      for (let m = re.exec(raw); null !== m && out.length < 5; m = re.exec(raw)) {
        out.push(mk(m[1], m[2], m[3], out.length));
      }
      return out;
    }
    return [];
  }

  // Blocco delle risposte per la seconda passata: il contenuto verbale entra SEMPRE (principio 2
  // della Specifica), i [Non ponibile] vengono dichiarati come esito «non chiarito».
  private _faseZeroRisposteTxt(): string {
    const righe = this.faseZeroTemiSignal()
      .map((t) => {
        const capo = `${t.temaId}. Tema: ${t.titolo}\nDomanda posta al cliente: ${t.domanda}`;
        if ('non_chiarito' === t.stato && 'non_ponibile' === t.origineNonChiarito) {
          return `${capo}\nChiarimento NON PONIBILE (cliente non più disponibile / tema chiuso / sessione conclusa): l'esito di questo tema è «non chiarito».${t.risposta.trim() ? `\nAppunti comunque raccolti: ${t.risposta.trim()}` : ''}`;
        }
        const conferma = t.confermatoRapido
          ? `\nIl somministratore ha marcato la risposta come inequivocabile: l'esito di questo tema è «chiarito in colloquio».`
          : '';
        return `${capo}\nRisposta del cliente: ${t.risposta.trim()}${conferma}`;
      })
      .join('\n\n');
    return `\n\n[RISPOSTE DI CHIARIMENTO — FASE ZERO, seconda passata]\n${righe}`;
  }

  // Registra gli esiti a valle della generazione (Rev. 9.3, obbligatoria): stato finale per ogni
  // tema + riconciliazione con la riga "Conteggio" del blocco "Esiti dei chiarimenti". In caso di
  // divergenza: log di errore e prevale il dato applicativo (Specifica §4).
  private _registraEsitiFaseZero(cliMd: string): void {
    const temi = this.faseZeroTemiSignal();
    const finali: IFaseZeroTema[] = temi.map((t) => {
      if ('non_chiarito' === t.stato) {
        return t; // [Non ponibile]: stato già finale, deciso dal somministratore
      }
      if (t.confermatoRapido) {
        return { ...t, stato: 'chiarito' as const };
      }
      // Rivalutazione del sistema: l'esito lo dichiara il blocco "Esiti dei chiarimenti".
      const stato = this._esitoDaBlocco(cliMd, t.titolo);
      return {
        ...t,
        stato: stato ?? ('chiarito' as const),
        origineNonChiarito: 'non_chiarito' === stato ? ('contrasto_persistente' as const) : null,
      };
    });
    const nChiariti = finali.filter((t) => 'chiarito' === t.stato).length;
    const nNonChiariti = finali.filter((t) => 'non_chiarito' === t.stato).length;
    const m =
      /Conteggio\s*:\s*(\d+)\s*tem\w*\s*congruent\w*\s*[·•,;-]?\s*(\d+)\s*chiarit\w*(?:\s*in\s*colloquio)?\s*[·•,;-]?\s*(\d+)\s*da\s*verificare/i.exec(
        cliMd,
      );
    let divergente = false;
    let nCongruenti = null === m ? 0 : Number(m[1]);
    if (null !== m && 0 === finali.length) {
      // Nessun tema passato dalla Fase Zero: nessun chiarimento in colloquio è possibile. Il
      // modello tende comunque a contare fra i "chiariti" i temi esaminati e risultati coerenti:
      // per definizione sono congruenti, non una divergenza.
      nCongruenti += Number(m[2]) + Number(m[3]);
    } else if (null === m) {
      if (0 === finali.length) {
        // Prompt senza blocco Esiti (versione pre-9.3 ancora attiva) e nessuna Fase Zero corsa:
        // il meccanismo è semplicemente inattivo, nessuna riga da registrare.
        this.faseZeroEsitiSignal.set(null);
        return;
      }
      // eslint-disable-next-line no-console -- Rev. 9.3 impone SEMPRE la riga di Conteggio
      console.error('[fase-zero] riga "Conteggio" assente dal profilo: prevale il dato applicativo');
      divergente = true;
    } else if (Number(m[2]) !== nChiariti || Number(m[3]) !== nNonChiariti) {
      // eslint-disable-next-line no-console -- riconciliazione richiesta dalla Specifica §4
      console.error(
        `[fase-zero] Conteggio divergente — prompt: ${m[2]} chiariti / ${m[3]} da verificare; app: ${nChiariti} / ${nNonChiariti}. Prevale il dato applicativo.`,
      );
      divergente = true;
    }
    this.faseZeroTemiSignal.set(finali);
    this.faseZeroEsitiSignal.set({
      temi: finali,
      nCongruenti,
      nChiariti,
      nNonChiariti,
      conteggioDivergente: divergente,
    });
  }

  // Esito di UN tema dalla riga corrispondente del blocco "Esiti dei chiarimenti" (match sul
  // titolo, case-insensitive). null se il blocco non nomina il tema: il default a monte è
  // «chiarito», coerente col profilo che a quel punto lo tratta da elemento pieno.
  private _esitoDaBlocco(cliMd: string, titolo: string): 'chiarito' | 'non_chiarito' | null {
    const idx = cliMd.indexOf('Esiti dei chiarimenti');
    if (-1 === idx) {
      return null;
    }
    const needle = titolo.trim().toLowerCase();
    const riga = cliMd
      .slice(idx)
      .split('\n')
      .find((l) => l.toLowerCase().includes(needle));
    if (undefined === riga) {
      // eslint-disable-next-line no-console -- nessun tema può mancare all'appello (Rev. 9.3)
      console.warn(`[fase-zero] tema "${titolo}" assente dal blocco Esiti: default «chiarito»`);
      return null;
    }
    return /non\s+chiarito/i.test(riga) ? 'non_chiarito' : 'chiarito';
  }

  // Esiti dei chiarimenti per il Prompt 2: dato applicativo reale, mai una stima del modello —
  // alimenta il "quadro di affidabilità dei dati" (temiCongruenti/temiChiariti/temiDaVerificare).
  // Applica verificaQuadroAffidabilita al markdown/JSON dell'Architettonico appena generato.
  // true = coerente (o non verificabile: niente esiti Fase Zero, o output non strutturato — quel
  // caso ha già il suo warning dedicato); false = bloccato, con toast e ritorno a `tornaA`.
  private _quadroCoerente(archMd: string, tornaA: Screen): boolean {
    const esiti = this.faseZeroEsitiSignal();
    const arch = this._parseArchProfilo(archMd);
    if (null === esiti || null === arch) {
      return true;
    }
    const err = verificaQuadroAffidabilita(arch, esiti);
    if (null === err) {
      return true;
    }
    console.error('[fase-zero] validazione incrociata Rev. 11.2 fallita:', err, arch);
    this._go(tornaA);
    this._error(
      `Il Profilo Architettonico non ha superato la validazione incrociata (${err}): il documento non è stato pubblicato, rigeneralo.`,
    );
    return false;
  }

  private _esitiTxt(): string {
    const esiti = this.faseZeroEsitiSignal();
    if (null === esiti) {
      // Specifica Prompt 2 Rev. 11.2 §2: profilo generato con Prompt 1 < 9.3 (nessun blocco
      // Esiti) e nessuna Fase Zero scattata — l'app non può ricostruire i congruenti, quindi
      // si passa la dicitura letterale prevista dal fallback, mai numeri inventati.
      return `\n\n[ESITI DEI CHIARIMENTI]\nEsiti non disponibili — profilo generato con versione precedente. Riporta questa dicitura TALE E QUALE nel Quadro di affidabilità dei dati e nei campi "temiCongruenti"/"temiChiariti"/"temiDaVerificare", senza inventare numeri.`;
    }
    const righe = esiti.temi
      .map(
        (t) =>
          `- ${t.titolo} — ${'chiarito' === t.stato ? 'chiarito in colloquio' : 'non chiarito — da verificare insieme'}`,
      )
      .join('\n');
    return `\n\n[ESITI DEI CHIARIMENTI — dato applicativo reale, mai una tua stima]\nConteggio: ${esiti.nCongruenti} temi congruenti · ${esiti.nChiariti} chiariti in colloquio · ${esiti.nNonChiariti} da verificare${righe ? `\n${righe}` : ''}\nUsa ESATTAMENTE questi valori nei campi "temiCongruenti", "temiChiariti" e "temiDaVerificare"; non stimarli tu. I temi «da verificare insieme» non ricevono prescrizioni né codici PA-§n: trattali solo come quesiti aperti — OGNI tema «da verificare» DEVE avere la sua voce in "domandeAperte", citandone il titolo testualmente (verifica incrociata obbligatoria, Rev. 11.2).`;
  }

  // Sostituisce (o aggiunge, se assente) la sezione "Indice degli elementi (PA-§n)" nel Profilo
  // Abitativo coi pesi finali REALI del cliente: composizione deterministica lato app, non un'edit
  // chiesta a un altro giro di LLM (v. commento in generateProfiloArchitettonico sul perché).
  private _finalizeCliMdIndice(cliMd: string, p: IPonderazione): string {
    const HEADING = 'Indice degli elementi (PA-§n)';
    if (0 === p.items.length) {
      return cliMd;
    }
    const righe = p.items
      .map((i) => `- ${i.codice} · ${i.statement} · peso ${i.finalPoints}/100 (confermato dal cliente)`)
      .join('\n');
    const sezione = `${HEADING}\n${righe}`;
    const idx = cliMd.indexOf(HEADING);
    const testa = -1 === idx ? cliMd : cliMd.slice(0, idx);
    return `${testa.trimEnd()}\n\n${sezione}`;
  }

  // Ponderazione dichiarata dal cliente (Nota §3): sostituisce, per questa rigenerazione, la
  // "TUA STIMA" che ARCH_JSON_FORMAT_*/legenda.peso chiede di norma — v. istruzione esplicita in
  // fondo al blocco. Vuoto se saltata senza che ci fossero elementi da pesare.
  private _pesiTxt(p: IPonderazione): string {
    if (p.skipped) {
      return `\n\n[PONDERAZIONE — saltata dal cliente]\nIl cliente ha rinunciato alla ponderazione: usa la sola sequenza tecnica per l'allocazione delle risorse e dichiaralo in nota. Non stimare tu i pesi.`;
    }
    if (0 === p.items.length) {
      return '';
    }
    const righe = p.items.map((i) => `${i.codice}: ${i.finalPoints}/100`).join('\n');
    return `\n\n[PONDERAZIONE DEL CLIENTE — dato reale, mai una tua stima]\n${righe}\nUsa questi valori esatti nel campo "peso" della legenda per i codici elencati; non stimarli tu. Aggiorna "prioritaBenessere" delle schede-ambiente coerentemente con questi pesi.`;
  }

  // Scostamento proposta↔corretto (Nota §3.3): dato di ascolto per il consulente, mai un blocco
  // né un dato mostrato al cliente — per ora solo loggato (v. Correzione, la UI della "una domanda
  // facoltativa" resta esplicitamente fuori scope finché non richiesta). "__top3__" è un codice
  // sentinella per il riordino dei primi tre elementi: non un vero PA-§n, non ha un "delta" reale.
  private _deviationFlags(items: readonly IPesoElemento[]): IDeviationFlag[] {
    const flags: IDeviationFlag[] = items
      .filter((i) => 15 <= Math.abs(i.finalPoints - i.proposedPoints))
      .map((i) => ({
        codice: i.codice,
        delta: i.finalPoints - i.proposedPoints,
        top3Reordered: false,
        outcome: null,
      }));
    const rank = (key: 'proposedPoints' | 'finalPoints'): string =>
      [...items]
        .sort((a, b) => b[key] - a[key])
        .slice(0, 3)
        .map((i) => i.codice)
        .join(',');
    if (rank('proposedPoints') !== rank('finalPoints')) {
      flags.push({ codice: '__top3__', delta: 0, top3Reordered: true, outcome: null });
    }
    if (flags.length) {
      // eslint-disable-next-line no-console -- dato di ascolto per il consulente, v. commento sopra
      console.info('[ponderazione] scostamento rilevato', flags);
    }
    return flags;
  }

  // Estrae l'Indice degli elementi (PA-§n) già scritto nel Profilo Abitativo e lo trasforma in
  // dati pesabili per la Ponderazione (Nota §3) --- una chiamata AI dedicata e leggera che
  // riserializza in JSON, non un parser regex sul markdown libero: il JSON passa dallo stesso
  // estrattore già affidabile usato per l'Architettonico (_extractJsonObject). Silenzioso su
  // liste vuote/malformate: la ponderazione resta comunque skippabile (Nota §3.2).
  private _seedPonderazioneFrom$(cliMd: string): Observable<void> {
    const sys =
      'Sei un estrattore dati preciso: non interpretare, non riassumere, copia fedelmente i valori già presenti nel testo.';
    const msg = `Dal Profilo Abitativo qui sotto, estrai SOLO la sezione finale "Indice degli elementi (PA-§n)". Restituisci SOLO un oggetto JSON valido (nessun testo prima o dopo, nessun code fence), con questa forma: {"items":[{"codice":"PA-01","statement":"... (l'elemento, fedele al testo, non riformulare)","proposedPoints": number (0-100: il peso proposto già scritto per quell'elemento; 0 se assente)}]}. Includi SOLO i codici PA-§n con un peso indicato; escludi i PD-n e ogni elemento "da verificare insieme". Se l'Indice non è presente o non contiene pesi, restituisci {"items":[]}.\n\n[PROFILO ABITATIVO]\n${cliMd}`;
    return this._api.generate(sys, msg, 4000).pipe(
      tap((raw) => {
        const parsed = this._extractJsonObject(raw, 'indiceElementi');
        const rows =
          (parsed?.['items'] as { codice?: string; statement?: string; proposedPoints?: number }[] | undefined) ?? [];
        const valid = rows.filter((r) => 'string' === typeof r.codice && 'string' === typeof r.statement);
        const rounded = this._roundWeightsTo100(valid.map((r) => r.proposedPoints ?? 0));
        const items: IPesoElemento[] = valid.map((r, idx) => ({
          codice: r.codice as string,
          statement: r.statement as string,
          proposedPoints: rounded[idx],
          finalPoints: rounded[idx],
        }));
        this._applyPonderazioneSeed(items);
      }),
      map(() => undefined),
    );
  }

  // Arrotonda i pesi proposti al multiplo di 5 più vicino MANTENENDO il totale a 100 (Nota §3.2:
  // "valori ammessi 0, 5, 10 … 100"): arrotondare ogni valore in isolamento può spostare la somma
  // lontano da 100 (es. 24+24+24+28=100 → 25+25+25+30=105) --- bloccando sia lo stepper (si muove
  // solo a scatti di 5) sia "Confermo la proposta", che diventano irrecuperabili senza poter
  // scrivere un valore libero. La somma di arrotondamenti a 5 è sempre un multiplo di 5, quindi lo
  // scarto da 100 lo è sempre: la correzione sposta l'eccesso/difetto sugli elementi con il resto
  // più vicino al cambio di direzione (metodo del resto maggiore), ciclando finché non è azzerato.
  private _roundWeightsTo100(raw: readonly number[]): number[] {
    if (0 === raw.length) {
      return [];
    }
    const rounded = raw.map((v) => Math.min(100, Math.max(0, Math.round(v / 5) * 5)));
    let drift = rounded.reduce((s, v) => s + v, 0) - 100;
    if (0 === drift) {
      return rounded;
    }
    const order = raw
      .map((v, i) => ({ i, rem: v - 5 * Math.floor(v / 5) }))
      .sort((a, b) => (drift > 0 ? a.rem - b.rem : b.rem - a.rem))
      .map((o) => o.i);
    for (let guard = 0; 0 !== drift && guard < 1000; guard += 1) {
      let moved = false;
      for (const i of order) {
        if (0 === drift) {
          break;
        }
        if (drift > 0 && rounded[i] > 0) {
          rounded[i] -= 5;
          drift -= 5;
          moved = true;
        } else if (drift < 0 && rounded[i] < 100) {
          rounded[i] += 5;
          drift += 5;
          moved = true;
        }
      }
      if (false === moved) {
        break; // tutti gli elementi già a 0/100: non c'è più spazio, caso limite raro
      }
    }
    return rounded;
  }

  private _applyPonderazioneSeed(items: IPesoElemento[]): void {
    this.ponderazioneSignal.set({
      items,
      total: items.reduce((s, i) => s + i.finalPoints, 0),
      confirmedAsProposed: false,
      skipped: false,
      // Pre-caricato coi nomi già inseriti (/intro o /document) — resta editabile in pagina.
      filledBy: this.partsSignal()
        .map((p) => p.name)
        .join(' e '),
      // Tracciamento del dispositivo di compilazione (Nota §3.1): non c'è un'identità client
      // separata in questa app a login unico, quindi lo user agent del dispositivo che apre la
      // schermata è il miglior proxy disponibile oggi.
      device: navigator.userAgent.slice(0, 200),
      openedAt: new Date().toISOString(),
      confirmedAt: null,
      deviationFlags: [],
    });
  }

  private _buildCtx(tr: string): string {
    const sd = this.schedaDocSignal();
    const schedaTxt =
      this.schedaModeSignal() === 'upload' && sd && 'application/pdf' === sd.mediaType
        ? `La Scheda dell'Abitazione è fornita come documento PDF allegato a questo messaggio ("${sd.name}"). Leggine contenuti e marcatori [D]/[R]/[V].`
        : this.schedaModeSignal() === 'upload' && sd && sd.extractedText
          ? sd.extractedText
          : this.schedaText();
    // Blocco G (Rev. 1 §11): percorso, ambienti coinvolti e impianti arrivano ai prompt come
    // blocco a sé. Assente sulle interviste mai avviate col nuovo flusso e in modalità documento:
    // in quel caso non si scrive nulla, meglio il silenzio di un perimetro inventato.
    const perimetro = this._perimetroText();
    const perimetroBlock =
      null === perimetro ? '' : `[PERIMETRO DELL'INTERVENTO]\n<perimetro>\n${perimetro}</perimetro>\n\n`;
    return `${perimetroBlock}[CONTESTO TECNICO — SCHEDA ABITATIVA]\n<dati_scheda>\n${schedaTxt}\n</dati_scheda>\n\n[TRASCRIZIONE / RISPOSTE DEL CLIENTE]\n<trascrizione>\n${tr}\n</trascrizione>`;
  }

  // Perimetro dell'intervento per i prompt. Ambienti coinvolti dai NOMI della Scheda (includono le
  // stanze aggiunte a mano, che non hanno un codice lookup); impianti dai codici fissati
  // all'avvio, tradotti in nome (+ opzione scelta) quando le lookup ci sono.
  private _perimetroText(): string | null {
    const start = this.interviewStartSignal();
    if (null === start) {
      return null;
    }
    const scheda = this.schedaSignal();
    const impiantiLookup = this._lookupsSignal()?.impianti ?? [];
    return buildPerimetroText({
      percorso: start.percorso_scelto,
      statoIntervento: scheda.statoIntervento,
      ambientiCoinvolti: (scheda.ambienti ?? [])
        .filter((a) => true === a.coinvolto)
        .map((a) => [a.tipo, a.nome].filter(Boolean).join(' — ') || '(ambiente)'),
      impianti: start.impianti.map((code) => {
        const l = impiantiLookup.find((i) => i.code === code);
        const opz = scheda.impiantiOpzioni?.[code];
        return { name: l?.name ?? code, opzione: l?.opzioni.find((o) => o.code === opz)?.name };
      }),
    });
  }

  // Il modello a volte incornicia il JSON in code fence o aggiunge testo prima/dopo nonostante
  // l'istruzione: si estrae la sottostringa tra la prima { e la sua } di chiusura (bilanciando le
  // graffe, per non fermarsi a una '}' dentro una stringa) invece di pretendere un match esatto.
  // Generica (non tipizzata IArchProfilo): usata sia sul profilo intero (regenProfile) sia su
  // ciascuna delle due metà di runFaseB, che da sole non sono un IArchProfilo valido.
  // Macchina a stati appiattita in guardie (house style: niente if annidati né else): ogni
  // carattere ricade in UN caso e chiude l'iterazione con continue — l'ordine delle guardie
  // È la logica (escape prima di tutto, poi lo stato "dentro stringa", poi i delimitatori).
  // ATTENZIONE: mirror in be/proxy-agcasabito/src/generation/generation.service.ts — allineati.
  private _extractJsonObject(raw: string, label: string): Record<string, unknown> | null {
    const start = raw.indexOf('{');
    if (-1 === start) {
      return null;
    }
    // Stack delle chiusure attese: `{` attende `}`, `[` attende `]`. Vuoto = oggetto radice chiuso.
    const open: string[] = [];
    let inStr = false;
    let escaped = false;
    for (let i = start; i < raw.length; i++) {
      const ch = raw[i];
      // escaped è vero solo dentro una stringa: il carattere corrente è "consumato" dall'escape.
      if (true === escaped) {
        escaped = false;
        continue;
      }
      if (true === inStr && '\\' === ch) {
        escaped = true;
        continue;
      }
      if (true === inStr) {
        // La stringa finisce solo su una doppia virgoletta non escapata.
        inStr = '"' !== ch;
        continue;
      }
      if ('"' === ch) {
        inStr = true;
        continue;
      }
      if ('{' === ch || '[' === ch) {
        open.push('{' === ch ? '}' : ']');
        continue;
      }
      if ('}' !== ch && ']' !== ch) {
        continue;
      }
      open.pop();
      if (0 !== open.length) {
        continue;
      }
      return this._parseCandidate(raw.slice(start, i + 1), label);
    }
    // Fine testo con parentesi aperte ma fuori da una stringa: il modello ha chiuso il turno
    // (end_turn) lasciando indietro solo le ultime chiusure (visto sul BE il 14/10/2026: mancava
    // la sola `}` finale di un cruscotto da 32k caratteri). Si aggiungono d'ufficio in ordine
    // inverso di apertura: se il testo era tronco a metà valore il parse fallisce comunque.
    const closers = open.reverse().join('');
    const closed = true === inStr ? null : this._parseCandidate(raw.slice(start) + closers, label, true);
    if (null !== closed) {
      // eslint-disable-next-line no-console -- diagnostica: recupero da segnalare, non silenzioso
      console.warn(`${label}: JSON senza le chiusure finali «${closers}», aggiunte d'ufficio:`, raw.slice(-120));
      return closed;
    }
    // eslint-disable-next-line no-console -- parentesi mai chiuse: quasi sempre un troncamento per
    // limite di token, non un errore di sintassi (v. environment.archMaxTokens).
    console.warn(`${label}: JSON troncato (parentesi non chiuse), ultimi caratteri:`, raw.slice(-300));
    return null;
  }

  // JSON.parse del candidato; al primo fallimento ritenta una volta riparando i controlli non
  // escapati dentro le stringhe e l'escape \' (abitudine JS del modello). Non indovina
  // virgolette o campi mancanti. quiet: niente warn, il chiamante ha un proprio fallback.
  private _parseCandidate(candidate: string, label: string, quiet = false): Record<string, unknown> | null {
    try {
      return JSON.parse(candidate) as Record<string, unknown>;
    } catch {
      // ritenta riparato
    }
    try {
      const repaired: string = candidate.replace(/"(?:[^"\\]|\\[\s\S])*"/g, (literal: string) =>
        // eslint-disable-next-line no-control-regex -- recupero mirato dei controlli JSON non escapati
        literal.replace(/[\u0000-\u001f]/g, (char: string) => JSON.stringify(char).slice(1, -1)),
      ).replace(/\\'/g, "'");
      return JSON.parse(repaired) as Record<string, unknown>;
    } catch (err) {
      if (false === quiet) {
        // eslint-disable-next-line no-console -- diagnostica: senza questo, un JSON malformato
        // (es. virgoletta non escapata in una citazione) è indistinguibile da un troncamento.
        console.warn(`${label}: JSON malformato`, err, candidate.slice(-300));
      }
      return null;
    }
  }

  private _parseArchProfilo(raw: string): IArchProfilo | null {
    return this._extractJsonObject(raw, 'archProfilo') as IArchProfilo | null;
  }

  // Il Profilo Abitativo è markdown libero, non JSON: non ha una rete di sicurezza come
  // _parseArchProfilo. Una risposta vuota o interrotta ora fa comunque fallire ApiService._generate
  // (v. Correzione 12/17), ma un testo arrivato completo e "vuoto solo in pratica" (poche righe,
  // nessun contenuto reale) passerebbe comunque il controllo: 50 caratteri è la soglia sotto la
  // quale un Profilo Abitativo vero non può stare (è sempre lungo diverse frasi).
  private _warnIfBlankCliMd(cliMd: string): void {
    if (cliMd.trim().length < 50) {
      this._warn(
        'Il Profilo Abitativo è arrivato vuoto o troppo corto: probabile errore del prompt o risposta interrotta. Prova a rigenerare.',
      );
    }
  }

  // Ricompone le due risposte parallele in un solo JSON IArchProfilo,
  // così tutto il resto della pipeline (parse/PDF/persistenza)
  // continua a vedere un archMd unico come prima dello split.
  private _mergeArchHalves(cruscottoRaw: string, ambientiRaw: string): string {
    const cruscotto = this._extractJsonObject(cruscottoRaw, 'archProfilo(cruscotto)');
    const ambienti = this._extractJsonObject(ambientiRaw, 'archProfilo(ambienti)');
    if (null === cruscotto || null === ambienti || false === Array.isArray(ambienti['ambienti'])) {
      throw new Error('Profilo Architettonico: JSON non valido; nessun PDF pubblicato. Rigenerare il profilo.');
    }
    const esiti: IFaseZeroEsiti | null = this.faseZeroEsitiSignal();
    // I conteggi sono dati applicativi: il modello genera solo il contenuto progettuale.
    if (null !== esiti) {
      cruscotto['temiCongruenti'] = String(esiti.nCongruenti);
      cruscotto['temiChiariti'] = String(esiti.nChiariti);
      cruscotto['temiDaVerificare'] = String(esiti.nNonChiariti);
    }
    return JSON.stringify({
      ...cruscotto,
      gradiente: ambienti['gradiente'],
      ambienti: ambienti['ambienti'],
    });
  }

  private _sysArch(nomi: string[]): string {
    return this.promptsSignal().architetto.replace(/\{NOMI\}/g, nomi.join(', '));
  }

  private _sysCli(nomi: string[]): string {
    return this.promptsSignal().cliente.replace(/\{NOMI\}/g, nomi.join(', '));
  }

  private _sourceScreen(): Screen {
    return this.sessionModeSignal() === 'document' ? 'document' : 'interview';
  }

  private _go(screen: Screen): void {
    // L'id della pratica è un segmento del path (/interview/<id>) sulle schermate del flusso,
    // finché esiste (v. _restoreFromUrl); il listener su NavigationEnd copre le navigazioni
    // fuori da _go.
    const id = this.interviewIdSignal();
    const cmd = null !== id && FLOW_SCREENS.has(screen) ? ['/', flowPath(screen), id] : ['/', screen];
    void this._router.navigate(cmd);
  }

  private _msg(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
  }

  // Fisher-Yates.
  private _shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  private _error(detail: string): void {
    this._toast.add({ severity: 'error', summary: 'Errore', detail, life: 6000 });
  }

  private _warn(detail: string): void {
    this._toast.add({ severity: 'warn', summary: 'Attenzione', detail, life: 5000 });
  }
}
